<template>
  <v-content class="gb sign-up_owner" :class="{ 'admin-inline': adminMode }">
    <v-container class="fill-width" fluid>
      <v-row align="center" justify="center">

        <!-- Billing success popup -->
        <v-dialog
            v-model="billing_success"
            persistent
            max-width="440"
        >
          <v-card class="success-dialog">
            <div class="text-center pa-8">
              <v-icon color="#19A99D" size="64" class="mb-4">fa-check-circle</v-icon>
              <h2 class="title_agent mb-3">Success!</h2>
              <p class="market-subtitle" style="margin: 0 auto 24px;">
                Your market is active and payment is confirmed.
              </p>
              <v-btn
                  depressed
                  rounded
                  class="btn-azul continue-btn"
                  height="48px"
                  @click="$router.push('/owner/owner-admin-panel')"
              >
                Next
                <v-icon right size="16">fa-arrow-right</v-icon>
              </v-btn>
            </div>
          </v-card>
        </v-dialog>

        <!-- Market selection form -->
        <v-card
            class="fill-width card-theme card-content custom-p-vendor-signup custom-container signup-form-container"
        >
          <!-- Stepper removed — drawing the market is a pre-wizard activity,
               not a numbered signup step. The actual signup wizard runs only
               on /sign_up (Step 1: Account) → /sign_up/billing (Step 2: Billing). -->

          <!-- Header -->
          <div class="header-row">
            <div>
              <h2 class="title_agent">{{ pageTitle }}</h2>
              <p class="market-subtitle">{{ subtitle_for_mode }}</p>
            </div>
            <div class="price-block">
              <span class="price-amount">${{ formatted_market_price }}</span>
              <span class="price-period">/month</span>
              <p class="price-note">$1 per 1,000 residents · Starts at $295/month</p>
            </div>
          </div>

          <v-divider class="mb-3 mt-1"></v-divider>

          <!-- Pre-decision value framing — visible only on the public
               kiey.com/my-market quote tool, sits between the calculated
               price and the map controls so the agent processes "what
               $X/month buys me" at the moment of price discovery, not
               after they've already mentally committed at /sign_up/billing.
               Hidden in authenticated and admin-embed modes (redundant
               for existing users / super-admins). -->
          <section v-if="isPublic && !isAdmin" class="my-market-includes">
            <h2 class="my-market-includes-title">What you get with Kiey</h2>
            <ul class="my-market-includes-list">
              <li><strong>Market exclusivity.</strong> No other team can serve homeowners in your ZIPs.</li>
              <li><strong>Connections.</strong> Push targeted vendor alerts to homeowners and drive bookings.</li>
              <li><strong>Kiey AI.</strong> 24/7 homeowner concierge; auto-routes to your vendors.</li>
              <li><strong>Communication.</strong> Real-time chat, in-app calling, voicemail, and file sharing.</li>
              <li><strong>Access.</strong> iOS, Android, desktop app, and web.</li>
              <li><strong>Admin panel.</strong> Team, vendor, and client management with analytics.</li>
            </ul>
          </section>

          <!-- Search + drawing controls on one row -->
          <div class="top-controls">
            <div class="search-wrapper">
              <i class="fa fa-map-marker-alt search-icon"></i>
              <input
                  ref="placesInput"
                  v-model="places_search"
                  type="text"
                  placeholder="Search your city or address to navigate"
                  class="search-input"
                  autocomplete="off"
              />
            </div>
            <div class="draw-buttons">
              <!-- Admin Edit multi-area controls. The super-user draws areas
                   one at a time; finished areas freeze into committed_areas
                   with a delete badge. "Add another area" works from both
                   the initial and done states (startAdditionalArea commits
                   any live polygon first). Admin grant + public flow are
                   untouched below (single-polygon). -->
              <template v-if="isAdminEdit">
                <template v-if="drawing_mode === 'drawing'">
                  <v-btn
                      outlined
                      rounded
                      color="#B71C1C"
                      class="draw-btn draw-btn-lg"
                      @click="cancelDrawing"
                  >
                    <v-icon size="14" left>fa-times</v-icon>
                    Cancel
                  </v-btn>
                  <v-btn
                      outlined
                      rounded
                      color="#061D47"
                      class="draw-btn draw-btn-lg"
                      :disabled="polygon_point_count === 0"
                      @click="undoVertex"
                  >
                    <v-icon size="14" left>fa-undo</v-icon>
                    Undo
                  </v-btn>
                </template>
                <template v-else>
                  <v-btn
                      outlined
                      rounded
                      small
                      color="#061D47"
                      class="draw-btn"
                      :disabled="!zip_data_ready"
                      :loading="!zip_data_ready"
                      @click="startAdditionalArea"
                  >
                    <v-icon size="14" left>fa-draw-polygon</v-icon>
                    {{ adminDrawButtonLabel }}
                  </v-btn>
                  <v-btn
                      v-if="committed_areas.length > 0 || drawing_mode === 'done'"
                      outlined
                      rounded
                      small
                      color="#B71C1C"
                      class="draw-btn"
                      @click="clearAllAreas"
                  >
                    <v-icon size="14" left>fa-redo</v-icon>
                    Start Over
                  </v-btn>
                </template>
              </template>
              <!-- Public draw controls — unchanged single-polygon flow. -->
              <template v-else>
                <v-btn
                    v-if="drawing_mode === 'initial'"
                    outlined
                    rounded
                    small
                    color="#061D47"
                    class="draw-btn"
                    :disabled="!zip_data_ready"
                    :loading="!zip_data_ready"
                    @click="startDrawing"
                >
                  <v-icon size="14" left>fa-draw-polygon</v-icon>
                  {{ zip_data_ready ? (isPublic ? 'Outline my market' : 'Click to draw your market') : 'Loading map data…' }}
                </v-btn>
                <template v-else-if="drawing_mode === 'drawing'">
                  <v-btn
                      outlined
                      rounded
                      color="#B71C1C"
                      class="draw-btn draw-btn-lg"
                      @click="cancelDrawing"
                  >
                    <v-icon size="14" left>fa-times</v-icon>
                    Cancel
                  </v-btn>
                  <v-btn
                      outlined
                      rounded
                      color="#061D47"
                      class="draw-btn draw-btn-lg"
                      :disabled="polygon_point_count === 0"
                      @click="undoVertex"
                  >
                    <v-icon size="14" left>fa-undo</v-icon>
                    Undo
                  </v-btn>
                </template>
                <v-btn
                    v-else-if="drawing_mode === 'done'"
                    outlined
                    rounded
                    small
                    color="#061D47"
                    class="draw-btn"
                    @click="clearPolygon"
                >
                  <v-icon size="14" left>fa-redo</v-icon>
                  Redraw
                </v-btn>
              </template>
              <!-- Mobile-only inline Claim CTA. Outlined-pill style while
                   disabled (matches Outline my market) and switches to a
                   solid Kiey-primary blue once a polygon is drawn so the
                   primary action visibly lights up. Hidden during
                   'drawing' since Cancel + Undo take that slot. -->
              <v-btn
                  v-if="$vuetify.breakpoint.smAndDown && drawing_mode !== 'drawing'"
                  :outlined="!canPrimaryCTA"
                  :depressed="canPrimaryCTA"
                  :dark="canPrimaryCTA"
                  rounded
                  small
                  color="#061D47"
                  class="draw-btn draw-btn-claim"
                  :class="{ 'btn-azul': canPrimaryCTA }"
                  :disabled="!canPrimaryCTA"
                  :loading="is_loading"
                  @click="onPrimaryCTA"
              >
                Claim This Market
                <v-icon right size="14">fa-arrow-right</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Map -->
          <div
              class="map-container mt-3"
              :class="{ 'has-readout': selected_zips.length > 0 || drawing_mode === 'drawing', 'drawing': drawing_mode === 'drawing' }"
          >
            <GmapMap
                ref="mapRef"
                :center="map_center"
                :zoom="map_zoom"
                :options="map_options"
                class="market-map"
            >
              <!-- Reserved territories — pins when zoomed out, polygons when zoomed in. -->
              <GmapMarker
                  v-for="(m, ownerId) in reservedAsPins"
                  :key="'pin-' + ownerId"
                  :position="hullCentroid(m.hull)"
                  :icon="reservedPinIcon"
                  @click="onTakenPolygonClick(m)"
              />
              <GmapPolygon
                  v-for="(m, ownerId) in reservedAsPolygons"
                  :key="'taken-' + ownerId"
                  :paths="m.hull"
                  :options="takenPolygonOptions"
                  @click="onTakenPolygonClick(m)"
                  @mouseover="onTakenMouseOver"
                  @mouseout="onTakenMouseOut"
              />
              <!-- Inline polygon labels: free → owner name, paid → "Reserved".
                   `label-zoom` is the first zoom at which the polygon's pixel
                   width is >= the pill's width — below that the pill would
                   visually exceed the polygon it's annotating, so we hide it. -->
              <MarketPolygonLabel
                  v-for="(m, ownerId) in reservedAsPolygons"
                  :key="'label-' + ownerId"
                  :position="hullCentroid(m.hull)"
                  :pricing-mode="m.pricing_mode"
                  :owner-name="m.owner_name || ''"
                  :current-zoom="map_current_zoom"
                  :label-zoom="m.label_zoom"
              />
              <!-- Office pin dropped at the signup address -->
              <GmapMarker
                  v-if="office_pin && google"
                  :position="office_pin"
                  :icon="office_pin_icon"
                  :clickable="false"
                  :z-index="5"
              />
            </GmapMap>
          </div>

          <!-- Readout -->
          <div v-if="selected_zips.length > 0 || drawing_mode === 'drawing' || has_shared_preview" class="readout">
            <div v-if="drawing_mode === 'drawing' && polygon_point_count < 3" class="readout-line readout-muted">
              Draw your area — population will update when you have 3 or more points.
            </div>
            <div v-else-if="selected_zips.length > 0 || has_shared_preview" class="readout-row">
              <div class="readout-line">
                Coverage: <strong>~{{ formatted_selected_population }}</strong> residents
                <span class="readout-sep">·</span>
                <template v-if="isAdminEdit">
                  <strong>{{ areaCountLabel }}</strong>
                  <span class="readout-sep">·</span>
                  <strong>{{ selected_zips.length }}</strong> ZIP{{ selected_zips.length === 1 ? '' : 's' }}
                </template>
                <template v-else>
                  Radius: <strong>{{ formatted_selection_radius }}</strong>
                </template>
              </div>
              <!-- Done sits on the right of the readout bar (same size/font as
                   Coverage/Radius) so agents see it as the next step required
                   before Claim This Market unlocks. Click finalizes the polygon
                   and opens the share popover. -->
              <v-menu
                  v-if="isPublic && !isAdmin"
                  v-model="share_open"
                  :close-on-content-click="false"
                  content-class="share-popover-wrap"
                  max-width="320"
                  offset-y
                  top
                  nudge-top="6"
              >
                <template #activator="{ attrs }">
                  <button
                      type="button"
                      class="readout-done-btn"
                      :disabled="!canSaveAndShare"
                      v-bind="attrs"
                      @click="saveAndShare"
                  >
                    Done <span class="readout-done-arrow" aria-hidden="true">&rarr;</span>
                  </button>
                </template>
                <v-card class="share-popover">
                  <h3 class="share-title">Save or Share Your Market</h3>
                  <p class="share-body">
                    Anyone who opens the link sees your exact market and price.
                  </p>
                  <div class="share-actions">
                    <v-btn block depressed small class="reserve-btn" color="#19A99D" dark @click="shareVia('reserve')">
                      <v-icon left size="14">fa-bookmark</v-icon>
                      <strong>Email Kiey to Reserve</strong>
                    </v-btn>
                    <div class="share-divider"><span>or just save it</span></div>
                    <v-btn block depressed small color="#061D47" dark @click="shareVia('email')">
                      <v-icon left size="14">fa-envelope</v-icon>
                      Email it to me
                    </v-btn>
                    <v-btn block outlined small color="#061D47" @click="shareVia('copy')">
                      <v-icon left size="14">fa-link</v-icon>
                      {{ copied ? 'Copied!' : 'Copy link' }}
                    </v-btn>
                  </div>
                </v-card>
              </v-menu>
              <!-- Admin mode mirrors the same Done finalize step, minus the
                   share popover. Identical behavior: closes the polygon and
                   triggers the ZIP computation; then the big bottom CTA
                   becomes the actual submit. After Done is pressed the
                   button stays visible but greyed + shows a check, so the
                   user can tell it's been actioned and the next step is the
                   Confirm CTA at the bottom. -->
              <button
                  v-if="isAdmin"
                  type="button"
                  class="readout-done-btn"
                  :class="{ 'is-finalized': drawing_mode === 'done' }"
                  :disabled="!canSaveAndShare || drawing_mode === 'done'"
                  @click="finishDrawing"
              >
                <template v-if="drawing_mode === 'done'">
                  <span aria-hidden="true">&#10003;</span> Done — now press Confirm
                </template>
                <template v-else>
                  Done <span class="readout-done-arrow" aria-hidden="true">&rarr;</span>
                </template>
              </button>
            </div>
            <!-- Admin Edit: per-area resident + ZIP breakdown. Only shown
                 when there's more than one area — for a single area the
                 main line above already says it all. -->
            <div v-if="isAdminEdit && areaBreakdown.length > 1" class="readout-areas">
              <div
                  v-for="a in areaBreakdown"
                  :key="a.label"
                  class="readout-area-line"
              >
                {{ a.label }}: <strong>~{{ a.population.toLocaleString('en-US') }}</strong> residents
                <span class="readout-sep">·</span>
                <strong>{{ a.zipCount }}</strong> ZIP{{ a.zipCount === 1 ? '' : 's' }}
              </div>
            </div>
            <div v-if="excluded_count > 0" class="readout-note">
              Part of this area is already owned by another agent and was excluded.
            </div>
          </div>

          <p class="territory-disclaimer">
            <v-icon size="12" color="#666" class="mr-1">fa-info-circle</v-icon>
            Agents, Vendors and Homeowners must be within your market radius to access your Kiey account.
          </p>

          <v-alert
              v-if="overlap_owner_names.length > 0"
              type="error"
              dense
              text
              class="mt-3"
          >
            Your territory crosses into {{ overlap_owner_names.join(', ') }}'s market. Redraw to continue.
          </v-alert>

          <v-alert
              v-if="self_overlap"
              type="error"
              dense
              text
              class="mt-3"
          >
            This area overlaps another of your areas. Redraw to continue.
          </v-alert>

          <v-alert
              v-if="error_message"
              type="error"
              dense
              text
              class="mt-2"
              dismissible
              @input="error_message = ''"
          >
            {{ error_message }}
          </v-alert>

          <!-- Actions -->
          <div class="actions">
            <div class="map-legend">
              <div class="legend-item">
                <span class="legend-swatch legend-reserved"></span>
                Reserved
              </div>
              <div v-if="office_pin" class="legend-item">
                <span class="legend-swatch legend-office"></span>
                Your office
              </div>
              <div v-if="drawing_mode !== 'initial'" class="legend-item">
                <span class="legend-swatch legend-yours"></span>
                Your market
              </div>
            </div>
            <v-btn v-if="!isPublic && !isAdmin" text rounded @click="signOut">SIGN OUT</v-btn>
            <v-btn v-if="isAdmin" text rounded @click="onAdminCancel">CANCEL</v-btn>
            <v-btn
                depressed
                rounded
                class="btn-azul continue-btn"
                height="48px"
                :disabled="!canPrimaryCTA"
                :loading="is_loading"
                @click="onPrimaryCTA"
            >
              {{ ctaLabel }}
              <v-icon right size="16">fa-arrow-right</v-icon>
            </v-btn>
          </div>
        </v-card>
      </v-row>
    </v-container>

    <div v-if="waitlist.open" class="wl-overlay" @click.self="closeWaitlist">
      <div class="wl-modal" role="dialog" aria-modal="true">
        <button type="button" class="wl-close" @click="closeWaitlist" aria-label="Close">×</button>
        <template v-if="!waitlist.success">
          <h3 class="wl-title">Join the waitlist</h3>
          <p class="wl-sub">
            This area is already taken<span v-if="waitlist.target_company_name">
              by <strong>{{ waitlist.target_company_name }}</strong></span>.
            Drop your details and we'll let you know if it opens up.
          </p>
          <div class="wl-row">
            <label>
              <span>First name</span>
              <input v-model="waitlist.first_name" type="text" autocomplete="given-name" />
            </label>
            <label>
              <span>Last name</span>
              <input v-model="waitlist.last_name" type="text" autocomplete="family-name" />
            </label>
          </div>
          <label class="wl-field">
            <span>Phone</span>
            <input v-model="waitlist.phone_number" type="tel" autocomplete="tel" />
          </label>
          <label class="wl-field">
            <span>Email</span>
            <input v-model="waitlist.email" type="email" autocomplete="email" />
          </label>
          <label class="wl-field">
            <span>Company / Brokerage</span>
            <input v-model="waitlist.company_name" type="text" autocomplete="organization" />
          </label>
          <label class="wl-field">
            <span>Address</span>
            <input
                ref="waitlistAddressInput"
                v-model="waitlist.address"
                type="text"
                autocomplete="off"
            />
          </label>
          <p v-if="waitlist.error" class="wl-error">{{ waitlist.error }}</p>
          <button
              type="button"
              class="wl-submit"
              :disabled="waitlist.submitting"
              @click="submitWaitlist"
          >
            {{ waitlist.submitting ? 'Submitting…' : 'Join waitlist' }}
          </button>
        </template>
        <template v-else>
          <h3 class="wl-title">You're on the list</h3>
          <p class="wl-sub">Thanks — we'll reach out the moment something changes in this area.</p>
          <button type="button" class="wl-submit" @click="closeWaitlist">Done</button>
        </template>
      </div>
    </div>
  </v-content>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import { gmapApi, Marker as GmapMarker, Polygon as GmapPolygon } from 'vue2-google-maps';
import MarketPolygonLabel from '@/components/markets/MarketPolygonLabel.vue';
import { floored_market_price, MARKET_PRICE_FLOOR } from '@/util/market_pricing';

export default {
  name: "MarketSelection",
  components: { GmapMarker, GmapPolygon, MarketPolygonLabel },

  props: {
    // When true, render in super-admin grant mode: hide signup chrome
    // (stepper, sign-out, share popover), change primary CTA to "Done",
    // and emit `done({ zips, population, calculated })` instead of
    // dispatching the signup claim/checkout flow.
    adminMode: { type: Boolean, default: false },
    adminTenantName: { type: String, default: '' },
    // Existing ZIP set when entering admin edit mode. Used only to fit
    // the map bounds to the tenant's current market — the user still
    // redraws from scratch, same as the public flow.
    adminInitialZips: { type: Array, default: () => [] },
    // Tenant whose market is being edited. Their existing ZIPs must be
    // excluded from the "taken / blocked" overlay so the super-user can
    // redraw over them.
    adminEditTenantId: { type: String, default: '' },
  },

  data: () => ({
    places_search: "",
    billing_success: false,
    error_message: "",
    // 'initial' (nothing drawn) | 'drawing' (adding points) | 'done' (polygon finalized, editable)
    drawing_mode: 'initial',
    // Reactive counter so the template updates as we push/pop vertices on the
    // underlying Polyline (Polyline paths are not themselves Vue-reactive).
    polygon_point_count: 0,
    excluded_count: 0,
    selection_radius_miles: 0,
    // Admin multi-area only: frozen areas the super-user has finished. Each
    // entry is { zips: string[], hull: [{lat,lng}] }. The live editable
    // polygon (_polygon) is NOT in here — it joins this list when the admin
    // starts another area or submits. Public signup never populates this.
    committed_areas: [],
    // ZIPs of the current live polygon (the not-yet-committed shape).
    // Reactive — kept in data() so the per-area readout updates as the live
    // polygon is drawn/edited. Updated at the tail of recomputeFromPath.
    live_zips: [],
    // { lat, lng } of the address the user entered at signup step 1
    office_pin: null,
    // Map of owner_tenant_id → owner_name for territories the drawn polygon
    // currently overlaps. Populated during recomputeFromPath; drives the
    // "you're crossing someone else's market" banner + Continue disable.
    overlap_owner_ids: {},
    // Admin Edit: true when the live polygon overlaps one of the admin's own
    // committed areas. Same banner + Continue-disable treatment as
    // overlap_owner_ids, just a different message.
    self_overlap: false,
    // Waitlist modal state — opened when a logged-out kiey.com/my-market
    // visitor clicks a Reserved polygon. POSTs to /v1/signup/agent_waitlist.
    waitlist: {
      open: false,
      target_tenant_id: null,
      target_company_name: null,
      first_name: '',
      last_name: '',
      phone_number: '',
      email: '',
      company_name: '',
      address: '',
      submitting: false,
      error: '',
      success: false,
    },
    // Share modal state (public quote page only)
    share_open: false,
    copied: false,
    // Populated when the page is opened with a ?m= query param (a shared
    // link from another user). Holds the decoded polygon + the recomputed
    // population so the price block shows the *sender's* price at a glance.
    shared_market_preview: null,
    // Default frame: continental US centroid at a zoom tight enough to avoid
    // flashing all of North America before fitBounds kicks in on first idle.
    // Mobile drops one zoom level so the country fits on first paint
    // (desktop zoom 5 only shows the Midwest on phone-width viewports).
    map_center: { lat: 39.5, lng: -98.35 },
    map_zoom: typeof window !== 'undefined' && window.innerWidth < 700 ? 3 : 5,
    // Live zoom read from the map — drives the pin-vs-polygon swap for
    // reserved territories.
    map_current_zoom: 5,
    map_options: {
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      clickableIcons: false,
      gestureHandling: 'greedy',
      draggableCursor: 'pointer',
      // Kill the ~300ms delay Google adds to every click while it waits for
      // a potential double-click. Single clicks become instant.
      disableDoubleClickZoom: true,
      keyboardShortcuts: false,
      styles: [
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "transit", stylers: [{ visibility: "off" }] },
      ],
    },
  }),

  // Non-reactive refs to Google Maps objects. Defining these in created()
  // (not data()) keeps Vue's reactivity system from walking Google's internal
  // map properties — that walk is slow and has been known to break vue2-
  // google-maps click listeners in some versions.
  created() {
    this._map = null;
    this._placesAutocomplete = null;
    this._drawingClickListener = null;
    this._polyline = null;
    this._polygon = null;
    this._vertexMarkers = [];
    this._polygonListeners = [];
    this._pendingRecomputeId = null;
    this._pendingRecomputeRAF = null;
    // Teal read-only polygon drawn when the page is opened via a shared ?m= link.
    this._sharedPolygon = null;
    // Imperative Google Maps overlays for each committed area, parallel to
    // `committed_areas` by index: { outline: Polygon, badge: Marker }.
    this._committedOverlays = [];
    // Guard so hydrateAdminAreas only runs once even though it retry-polls.
    this._adminAreasHydrated = false;
  },

  computed: {
    google: gmapApi,
    // Admin mode can be triggered two ways:
    //   1. Direct prop binding (when MarketSelection is rendered as a child
    //      Vue component — the inline-embed path).
    //   2. `?admin=1` query string (when this page is loaded inside an
    //      iframe from the super-admin Markets surface — the literal
    //      kiey.com/my-market embed path).
    // `isAdmin` and `effectiveAdminTenantName` collapse both into one
    // signal everything else in the component reads.
    isAdmin() {
      if (this.adminMode) return true;
      return !!(this.$route && this.$route.query && this.$route.query.admin === '1');
    },
    // Multi-area drawing is scoped to the super-admin "Edit market" flow
    // only — signaled by a non-empty adminEditTenantId. Admin "Add Market"
    // (grant) and the public signup flow stay single-polygon.
    isAdminEdit() {
      return this.isAdmin && !!this.adminEditTenantId;
    },
    effectiveAdminTenantName() {
      if (this.adminTenantName) return this.adminTenantName;
      const q = this.$route && this.$route.query;
      if (q && q.tenant_name) {
        try { return decodeURIComponent(q.tenant_name); } catch (e) { return q.tenant_name; }
      }
      return '';
    },
    adminTenantId() {
      if (this.$route && this.$route.query && this.$route.query.tenant_id) {
        return this.$route.query.tenant_id;
      }
      return null;
    },
    ...mapState({
      selected_zips: (state) => state.Market_billing.selected_zips,
      zip_data: (state) => state.Market_billing.zip_data,
      store_status: (state) => state.Market_billing.status,
    }),
    ...mapGetters('Market_billing', [
      'is_loading',
      'selected_population',
      'taken_markets_by_owner',
      'zip_population_map',
    ]),

    // Admin edit mode: hide the edited tenant's own market from the
    // "taken / blocked" overlay so the super-user can redraw over it.
    // Match on owner_tenant_id (not the dict key) so every area of a
    // multi-area edited tenant is filtered.
    visibleTakenMarkets() {
      const all = this.taken_markets_by_owner || {};
      if (!this.adminEditTenantId) return all;
      const out = {};
      for (const key of Object.keys(all)) {
        if (all[key].owner_tenant_id === this.adminEditTenantId) continue;
        out[key] = all[key];
      }
      return out;
    },

    takenPolygonOptions() {
      return {
        strokeColor: '#B71C1C',
        strokeOpacity: 0.95,
        strokeWeight: 2,
        fillColor: '#B71C1C',
        fillOpacity: 0.28,
        // Absorb clicks so vertices can't be placed inside a taken territory
        // and show a not-allowed cursor when hovering over reserved areas.
        clickable: true,
        cursor: 'not-allowed',
        zIndex: 5,
      };
    },

    overlap_owner_names() {
      return Object.keys(this.overlap_owner_ids)
        .map(id => this.overlap_owner_ids[id])
        .filter(Boolean);
    },

    hasOverlap() {
      return this.overlap_owner_names.length > 0 || this.self_overlap;
    },

    // $1 per 1,000 residents inside the drawn market, rounded up so we never
    // undercharge (e.g. 293,056 residents → $294/mo), then raised to the
    // monthly floor. Returns 0 for an undrawn market so the UI shows "—".
    market_price() {
      return floored_market_price(this.effective_population || 0);
    },

    formatted_market_price() {
      if (this.effective_population <= 0) return '—';
      return this.market_price.toLocaleString('en-US');
    },

    // Each market has its own dot→polygon swap zoom computed from the
    // size of its territory. Big markets become an outline at country
    // zoom; small ones stay a dot until you zoom in. Without this the
    // global zoom-10 swap meant huge markets never showed an outline
    // (you were already inside the polygon by zoom 10) and tiny ones
    // showed an outline that was just a speck.
    reservedAsPins() {
      const out = {};
      for (const [k, m] of Object.entries(this.visibleTakenMarkets)) {
        if (this.map_current_zoom < (m.swap_zoom || 10)) out[k] = m;
      }
      return out;
    },
    reservedAsPolygons() {
      const out = {};
      for (const [k, m] of Object.entries(this.visibleTakenMarkets)) {
        if (this.map_current_zoom >= (m.swap_zoom || 10)) out[k] = m;
      }
      return out;
    },

    // Small red circle pin used at the centroid of a reserved territory
    // when the map is zoomed out too far to make sense of the polygon.
    reservedPinIcon() {
      if (!this.google) return undefined;
      return {
        path: this.google.maps.SymbolPath.CIRCLE,
        scale: 9,
        fillColor: '#B71C1C',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3,
      };
    },

    // Red pin drawn at the user's office (from signup step 1). Falls back to
    // a plain red dot if Google Maps hasn't loaded yet.
    office_pin_icon() {
      if (!this.google) return undefined;
      return {
        path: this.google.maps.SymbolPath.CIRCLE,
        scale: 9,
        fillColor: '#D0021B',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      };
    },

    formatted_selected_population() {
      return this.effective_population.toLocaleString('en-US');
    },

    zip_data_ready() {
      return !!(this.zip_data && this.zip_data.data);
    },

    formatted_selection_radius() {
      const r = this.selection_radius_miles || 0;
      if (r <= 0) return '0 mi';
      return `${r.toFixed(r < 10 ? 1 : 0)} mi`;
    },

    canContinue() {
      // Admin Edit multi-area: committed areas alone are enough to submit,
      // even when there's no live polygon in the 'done' state.
      if (this.isAdminEdit && this.committed_areas.length > 0 && !this.hasOverlap) {
        return true;
      }
      return this.drawing_mode === 'done'
          && this.selected_zips.length > 0
          && !this.hasOverlap;
    },

    // Admin draw button label — "Add another area" once at least one area
    // exists (committed or a finished live polygon), else the first-draw copy.
    adminDrawButtonLabel() {
      if (!this.zip_data_ready) return 'Loading map data…';
      if (this.committed_areas.length > 0 || this.drawing_mode === 'done') {
        return 'Add another area';
      }
      return 'Draw market area';
    },

    // Admin readout: count committed areas plus the live polygon if it's
    // finished or already has a valid shape mid-draw.
    areaCountLabel() {
      const live = (this.drawing_mode === 'done'
        || (this.drawing_mode === 'drawing' && this.polygon_point_count >= 3)) ? 1 : 0;
      const n = this.committed_areas.length + live;
      return `${n} area${n === 1 ? '' : 's'}`;
    },

    // Admin Edit readout: per-area resident + ZIP counts. The committed
    // areas first (each carries its own zips), then the live polygon being
    // drawn/edited as the trailing area. Population is summed from the
    // shared zip_population_map getter.
    areaBreakdown() {
      const popMap = this.zip_population_map || {};
      const popOf = (zips) => {
        let total = 0;
        for (const z of (zips || [])) total += popMap[z] || 0;
        return total;
      };
      const out = this.committed_areas.map((area, i) => ({
        label: `Area ${i + 1}`,
        population: popOf(area.zips),
        zipCount: (area.zips || []).length,
      }));
      const liveCounts = (this.drawing_mode === 'done'
        || (this.drawing_mode === 'drawing' && this.polygon_point_count >= 3));
      if (liveCounts && this.live_zips.length > 0) {
        out.push({
          label: `Area ${out.length + 1}`,
          population: popOf(this.live_zips),
          zipCount: this.live_zips.length,
        });
      }
      return out;
    },

    // Save and Share is active as soon as the polygon has enough points to be
    // a valid shape (3+) and doesn't cross a reserved territory. In drawing
    // mode we accept it; the button finalizes before opening the menu.
    canSaveAndShare() {
      if (this.hasOverlap) return false;
      if (this.drawing_mode === 'drawing') return this.polygon_point_count >= 3;
      if (this.drawing_mode === 'done') return this.selected_zips.length > 0;
      return false;
    },

    // Public quote mode — keyed off the route name so there are two URLs
    // (/select_market for authenticated signup, /my-market for public quote).
    isPublic() {
      return this.$route.name === 'my_market';
    },

    pageTitle() {
      if (this.isAdmin) {
        return this.effectiveAdminTenantName
          ? `Drawing market for ${this.effectiveAdminTenantName}`
          : 'Drawing market';
      }
      return this.isPublic ? 'Your Market. Your Price.' : 'Pick Your Market';
    },

    subtitle_for_mode() {
      if (this.drawing_mode === 'drawing') {
        const n = this.polygon_point_count;
        if (n === 0) return 'Click the map to drop your first point. Press Done when ready, or Esc to cancel.';
        if (n < 3) return `${n} of 3+ points placed. Keep clicking to outline your territory.`;
        return `${n} points placed. Press Done when ready, or Esc to cancel.`;
      }
      if (this.drawing_mode === 'done') {
        return 'Drag any corner to adjust. Drag the midpoint dots to add new corners. Redraw to start over.';
      }
      if (this.isPublic && this.has_shared_preview) {
        return 'This market was shared with you. Draw your own to see what yours costs.';
      }
      if (this.isPublic) return 'Draw your exclusive territory. See your monthly price instantly.';
      if (this.isAdmin) return 'Search a city, then draw the exact area to grant this tenant.';
      return 'Search your city, then draw the exact area you want to cover.';
    },

    // Headline shown inside the popover itself.
    share_text() {
      return 'I made my market on Kiey — check yours and see what it costs.';
    },

    // Returns /my-market with the current polygon encoded as ?m=<base64 JSON>.
    // Reading `polygon_point_count` + `selection_radius_miles` keeps this
    // reactive — both are bumped whenever the polygon is edited.
    share_url() {
      if (typeof window === 'undefined') return '';
      void this.polygon_point_count;
      void this.selection_radius_miles;
      const base = window.location.origin + '/my-market';

      // Extract vertices from whichever shape is currently live — the
      // editable polygon after Done, or the in-progress polyline while drawing.
      const source = this._polygon || this._polyline;
      if (!source) return base;
      const path = source.getPath();
      if (!path || path.getLength() < 3) return base;

      const vertices = [];
      for (let i = 0; i < path.getLength(); i++) {
        const p = path.getAt(i);
        vertices.push([+p.lat().toFixed(5), +p.lng().toFixed(5)]);
      }
      try {
        const json = JSON.stringify({ p: vertices });
        // btoa needs ASCII. unescape(encodeURIComponent(...)) round-trips UTF-8
        // safely in older browsers too.
        const encoded = btoa(unescape(encodeURIComponent(json)));
        return `${base}?m=${encodeURIComponent(encoded)}`;
      } catch (e) {
        return base;
      }
    },

    share_messages() {
      const url = this.share_url;
      return {
        email: {
          subject: 'My Kiey market',
          body:
            "Saving this for later.\n\n" +
            "My market on Kiey:\n" +
            url + "\n",
        },
      };
    },

    // True once a shared link (?m=...) has been successfully decoded into a
    // preview polygon. Drives the CTA label swap and the subtitle copy.
    has_shared_preview() {
      return !!(
        this.shared_market_preview &&
        this.shared_market_preview.vertices &&
        this.shared_market_preview.vertices.length >= 3
      );
    },

    // Population used by the price block. Prefers the user's own selection;
    // falls back to the decoded shared-preview population so recipients see
    // the sender's price at a glance.
    effective_population() {
      if (this.selected_zips && this.selected_zips.length > 0) return this.selected_population;
      if (this.has_shared_preview) return this.shared_market_preview.population || 0;
      return 0;
    },

    // Bottom CTA label — flips to "Draw my own market" when the user opened a
    // shared link and hasn't drawn anything yet.
    ctaLabel() {
      // Admin: small "Done" in the readout closes the polygon (same as
      // kiey.com); this big CTA is the actual submit, matching the public
      // "Claim This Market" position.
      if (this.isAdmin) return 'Confirm';
      if (!this.isPublic) return 'Continue to Billing';
      if (this.has_shared_preview && this.drawing_mode === 'initial') return 'Draw my own market';
      return 'Claim This Market';
    },

    canPrimaryCTA() {
      if (this.isPublic && this.has_shared_preview && this.drawing_mode === 'initial') return true;
      return this.canContinue;
    },
  },

  async mounted() {
    // Body overflow lock is for the fullscreen page experience — we don't
    // want kiey.com /my-market scrolling behind the map. When embedded in
    // admin (the parent page owns scrolling), don't touch body overflow.
    // On small screens the card content overflows the viewport, so we must
    // let the page scroll naturally — otherwise the bottom Claim CTA gets
    // clipped below the fold with no way to reach it.
    if (!this.isAdmin) {
      this._lockBodyOverflow = window.innerWidth > 700;
      if (this._lockBodyOverflow) {
        this._prevBodyOverflow = document.body.style.overflow;
        this._prevHtmlOverflow = document.documentElement.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      }
    }

    this._escHandler = (e) => {
      if (e.key === 'Escape' && this.drawing_mode === 'drawing') {
        this.cancelDrawing();
      }
    };
    window.addEventListener('keydown', this._escHandler);

    this.$store.dispatch('Market_billing/load_zip_data');
    this.$store.dispatch('Market_billing/fetch_markets').then(() => {
      // Dev helper: add ?test_markets=1 to seed three fake reserved
      // territories so you can verify rendering + overlap blocking.
      if (this.$route.query.test_markets === '1') this.injectTestMarkets();
    });
    // Live-link this map to admin Markets via Firestore — any change
    // anywhere reflects here in ~1s with no refresh.
    this.$store.dispatch('Market_billing/subscribe_markets');

    const billing = this.$route.query.billing;
    if (billing === 'success') {
      this.billing_success = true;
      // Signup is done — wipe the cached address so a different user on the
      // same browser can't have their map framed to the previous country.
      try { localStorage.removeItem('signup_address'); } catch (e) { /* noop */ }
    } else if (billing === 'canceled') {
      this.error_message = 'Payment was canceled. You can try again.';
    }

    // Set the initial frame (US or CA) before the map mounts so there's
    // no North-America flash.
    this.frameMapToSignupCountry();

    if (this.$refs.mapRef) {
      this.$refs.mapRef.$mapPromise.then((mapInstance) => {
        this._map = mapInstance;
        this.initPlacesAutocomplete();
        // Mobile: fit bounds to the whole country so phones reliably show
        // the entire continental US (zoom-level approximations crop
        // unevenly across phone widths and viewport DPRs). Skip on
        // desktop and on shared/preview/admin entries which already
        // pan to a specific area.
        if (
          typeof window !== 'undefined' &&
          window.innerWidth < 700 &&
          this.google &&
          !this.$route.query.m &&
          !(this.adminMode && this.adminInitialZips && this.adminInitialZips.length)
        ) {
          try {
            let country = 'US';
            try {
              const raw = localStorage.getItem('signup_address');
              if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && parsed.country) country = parsed.country.toUpperCase();
              }
            } catch (_) { /* noop */ }
            const bounds = new this.google.maps.LatLngBounds(
              country === 'CA' ? { lat: 41, lng: -141 } : { lat: 24.4, lng: -125 },
              country === 'CA' ? { lat: 60, lng: -52 } : { lat: 49.4, lng: -66.9 }
            );
            mapInstance.fitBounds(bounds, 0);
          } catch (_) { /* noop */ }
        }
        // Track zoom so we can swap reserved territory visuals between
        // a single pin (zoomed out) and the full polygon (zoomed in).
        this.map_current_zoom = mapInstance.getZoom() || this.map_zoom;
        mapInstance.addListener('zoom_changed', () => {
          this.map_current_zoom = mapInstance.getZoom();
        });
        // If the URL carries a shared polygon (?m=...), draw it as a
        // read-only preview now that the map is ready.
        const sharedParam = this.$route.query.m;
        if (sharedParam) this.loadSharedMarket(sharedParam);
        // Admin edit mode: pan/zoom to where the tenant's current market
        // sits so the super-user isn't staring at a country-wide map.
        if (this.adminMode && this.adminInitialZips && this.adminInitialZips.length) {
          this.frameMapToInitialZips();
        }
        // Admin Edit: hydrate the tenant's existing areas as committed areas
        // the super-user can keep / delete / add to.
        if (this.adminEditTenantId) {
          this.hydrateAdminAreas();
        }
      });
    }
  },

  beforeDestroy() {
    if (!this.isAdmin && this._lockBodyOverflow) {
      document.body.style.overflow = this._prevBodyOverflow || '';
      document.documentElement.style.overflow = this._prevHtmlOverflow || '';
    }
    if (this._escHandler) window.removeEventListener('keydown', this._escHandler);
    if (this._pacBodyObserver) this._pacBodyObserver.disconnect();
    if (this._pacObserverTimeout) clearTimeout(this._pacObserverTimeout);
    // Put the pac-container back on <body> so Google can reuse it cleanly.
    const pac = document.querySelector('.pac-container.kiey-pac-below');
    if (pac) document.body.appendChild(pac);
    this.cancelPendingRecompute();
    this.teardownAllOverlays();
    if (this._sharedPolygon) {
      this._sharedPolygon.setMap(null);
      this._sharedPolygon = null;
    }
  },

  watch: {
    zip_data(newVal) {
      // If the polygon was finalized before the ZIP data finished loading,
      // retry the compute once it lands.
      if (newVal && this.drawing_mode === 'done') {
        this.recomputeSelection();
      }
    },
  },

  methods: {
    ...mapActions('Market_billing', [
      'set_selection',
      'checkout',
    ]),

    // Frame the initial map view to the country of the address the user
    // entered at signup (step 1) AND drop a pin on their office address.
    // Uses reactive center/zoom (not fitBounds) so vue2-google-maps' own
    // two-way binding doesn't revert the view right after we set it.
    // Centroid of the hull (simple average of vertices), used as the anchor
    // for the RESERVED stamp marker.
    hullCentroid(hull) {
      if (!hull || !hull.length) return { lat: 0, lng: 0 };
      let lat = 0, lng = 0;
      for (const p of hull) { lat += p.lat; lng += p.lng; }
      return { lat: lat / hull.length, lng: lng / hull.length };
    },

    // Clicks over a reserved territory are absorbed by the polygon (never
    // become vertices). On the public /my-market page (not adminMode and
    // not while drawing), open a waitlist contact form so the prospect
    // can register interest in that area. In admin or drawing mode, just
    // surface a brief banner so the user knows WHY their click didn't
    // register a vertex.
    onTakenPolygonClick(market) {
      if (this.adminMode || this.drawing_mode === 'drawing') {
        this.flashTakenError('This area is reserved.');
        return;
      }
      this.waitlist = {
        open: true,
        target_tenant_id: (market && market.owner_tenant_id) || null,
        target_company_name: (market && market.owner_name) || null,
        first_name: '',
        last_name: '',
        phone_number: '',
        email: '',
        company_name: '',
        address: '',
        address_picked: false,
        submitting: false,
        error: '',
        success: false,
      };
      // Wire up Google Places autocomplete for the Address field once
      // Vue mounts the modal. The map already loaded the Maps JS SDK,
      // so window.google is available without an extra script load.
      this.$nextTick(() => this.initWaitlistAddressAutocomplete());
    },

    initWaitlistAddressAutocomplete() {
      if (this._waitlistAddressAc) return;
      const input = this.$refs.waitlistAddressInput;
      if (!input || !this.google || !this.google.maps || !this.google.maps.places) return;
      const ac = new this.google.maps.places.Autocomplete(input, {
        types: ['address'],
        componentRestrictions: { country: ['us', 'ca'] },
        fields: ['formatted_address', 'geometry'],
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (place && place.formatted_address) {
          this.waitlist.address = place.formatted_address;
        }
        this.waitlist.address_picked = !!(place && place.geometry);
      });
      input.addEventListener('input', () => {
        this.waitlist.address_picked = false;
      });
      // Google's Autocomplete widget force-sets placeholder = "Enter a
      // location" on the input right after binding. Clear it and watch
      // for any future re-writes from the SDK.
      input.placeholder = '';
      const obs = new MutationObserver(() => {
        if (input.placeholder && input.placeholder !== '') input.placeholder = '';
      });
      obs.observe(input, { attributes: true, attributeFilter: ['placeholder'] });
      this._waitlistAddressAc = ac;
      this._waitlistPlaceholderObserver = obs;
    },

    closeWaitlist() {
      this.waitlist.open = false;
      if (this._waitlistPlaceholderObserver) {
        this._waitlistPlaceholderObserver.disconnect();
        this._waitlistPlaceholderObserver = null;
      }
      this._waitlistAddressAc = null;
    },

    async submitWaitlist() {
      const w = this.waitlist;
      if (
        !w.first_name.trim() ||
        !w.last_name.trim() ||
        !w.phone_number.trim() ||
        !w.email.trim() ||
        !w.company_name.trim() ||
        !w.address.trim()
      ) {
        w.error = 'Please fill in all fields.';
        return;
      }
      if (!w.address_picked) {
        w.error = 'Please pick your address from the dropdown suggestions.';
        return;
      }
      w.submitting = true;
      w.error = '';
      try {
        const axios = (await import('axios')).default;
        const config = (await import('@/config/constants')).default;
        await axios.post(config.api_url + 'signup/agent_waitlist', {
          first_name: w.first_name.trim(),
          last_name: w.last_name.trim(),
          phone_number: w.phone_number.trim(),
          email: w.email.trim(),
          company_name: w.company_name.trim(),
          address: w.address.trim(),
          target_tenant_id: w.target_tenant_id,
        });
        w.success = true;
      } catch (e) {
        const errs = e && e.response && e.response.data && e.response.data.errors;
        w.error = (errs && Object.values(errs).flat().join(', ')) || (e && e.message) || 'Something went wrong.';
      } finally {
        w.submitting = false;
      }
    },

    flashTakenError(msg) {
      this.error_message = msg;
      if (this._takenErrTimeout) clearTimeout(this._takenErrTimeout);
      this._takenErrTimeout = setTimeout(() => {
        this.error_message = '';
        this._takenErrTimeout = null;
      }, 2500);
    },

    // Returns the owner name of the first reserved territory whose hull is
    // crossed by segment p1→p2, or null if clear. Uses 20-sample point-in-
    // polygon — good enough to catch any segment that physically cuts through
    // a reserved area. Caches Google Polygon objects per owner on `this` so
    // we're not rebuilding them on every click.
    segmentCrossesTaken(p1, p2) {
      if (!this.google || !this.google.maps.geometry) return null;
      const contains = this.google.maps.geometry.poly.containsLocation;
      const markets = this.visibleTakenMarkets;
      if (!this._hullPolyCache) this._hullPolyCache = {};
      const SAMPLES = 20;
      const lat1 = p1.lat(), lng1 = p1.lng();
      const lat2 = p2.lat(), lng2 = p2.lng();

      for (const [ownerId, m] of Object.entries(markets)) {
        if (!m.hull || m.hull.length < 3) continue;
        let poly = this._hullPolyCache[ownerId];
        if (!poly) {
          poly = new this.google.maps.Polygon({ paths: m.hull });
          this._hullPolyCache[ownerId] = poly;
        }
        for (let i = 0; i <= SAMPLES; i++) {
          const t = i / SAMPLES;
          const pt = new this.google.maps.LatLng(
            lat1 + (lat2 - lat1) * t,
            lng1 + (lng2 - lng1) * t,
          );
          if (contains(pt, poly)) {
            return m.owner_name || 'another agent';
          }
        }
      }
      return null;
    },

    // Same sampled-segment test as segmentCrossesTaken, but against the
    // admin's own committed areas — so a new/edited area can't be drawn
    // across one that's already committed. Committed-area hulls change as
    // areas are added/edited/removed, so the Polygons are rebuilt per call
    // (committed_areas is small; this isn't a hot path).
    segmentCrossesCommittedArea(p1, p2) {
      if (!this.google || !this.google.maps.geometry) return false;
      if (!this.committed_areas.length) return false;
      const contains = this.google.maps.geometry.poly.containsLocation;
      const SAMPLES = 20;
      const lat1 = p1.lat(), lng1 = p1.lng();
      const lat2 = p2.lat(), lng2 = p2.lng();
      for (const area of this.committed_areas) {
        if (!area.hull || area.hull.length < 3) continue;
        const poly = new this.google.maps.Polygon({ paths: area.hull });
        for (let i = 0; i <= SAMPLES; i++) {
          const t = i / SAMPLES;
          const pt = new this.google.maps.LatLng(
            lat1 + (lat2 - lat1) * t,
            lng1 + (lng2 - lng1) * t,
          );
          if (contains(pt, poly)) return true;
        }
      }
      return false;
    },

    onTakenMouseOver() {
      // Backup cursor change in case the polygon-level `cursor` option isn't
      // honored by the current browser. Restored on mouseout.
      if (this._map && !this._takenCursorActive) {
        this._takenCursorActive = true;
        this._prevCursor = this._map.get('draggableCursor');
        this._map.setOptions({ draggableCursor: 'not-allowed' });
      }
    },

    onTakenMouseOut() {
      if (this._map && this._takenCursorActive) {
        this._takenCursorActive = false;
        this._map.setOptions({ draggableCursor: this._prevCursor || (this.drawing_mode === 'drawing' ? 'crosshair' : 'pointer') });
      }
    },

    // DEV-ONLY: inject three fake taken-market neighborhoods so we can verify
    // the reserved-territory overlay + overlap-block behavior without needing
    // live Firestore data. Triggered by ?test_markets=1 in the URL.
    injectTestMarkets() {
      const fakes = [
        // A block of ZIPs around Dallas, TX
        { owner_name: 'Dallas Demo Agent', owner_tenant_id: 'demo-tenant-dallas',
          zips: [
            { code: '75201', lat: 32.7869, lng: -96.7979 },
            { code: '75202', lat: 32.7775, lng: -96.8037 },
            { code: '75204', lat: 32.8012, lng: -96.7857 },
            { code: '75206', lat: 32.8324, lng: -96.7708 },
            { code: '75219', lat: 32.8176, lng: -96.8116 },
          ] },
        // A block around Denver, CO
        { owner_name: 'Denver Demo Agent', owner_tenant_id: 'demo-tenant-denver',
          zips: [
            { code: '80202', lat: 39.7528, lng: -104.9975 },
            { code: '80203', lat: 39.7306, lng: -104.9803 },
            { code: '80204', lat: 39.7348, lng: -105.0178 },
            { code: '80205', lat: 39.7525, lng: -104.9678 },
            { code: '80206', lat: 39.7330, lng: -104.9489 },
          ] },
        // A block around Atlanta, GA
        { owner_name: 'Atlanta Demo Agent', owner_tenant_id: 'demo-tenant-atlanta',
          zips: [
            { code: '30303', lat: 33.7537, lng: -84.3863 },
            { code: '30308', lat: 33.7711, lng: -84.3802 },
            { code: '30309', lat: 33.7989, lng: -84.3896 },
            { code: '30312', lat: 33.7463, lng: -84.3714 },
            { code: '30318', lat: 33.7918, lng: -84.4326 },
          ] },
      ];
      const fake_rows = [];
      for (const m of fakes) {
        for (const z of m.zips) {
          fake_rows.push({
            code: z.code,
            region: 'US',
            name: `${z.code} – Demo`,
            is_own: false,
            owner_tenant_id: m.owner_tenant_id,
            owner_name: m.owner_name,
            centroid_lat: z.lat,
            centroid_lng: z.lng,
          });
        }
      }
      // Merge into whatever the API returned
      const current = this.$store.state.Market_billing.taken_zips;
      const merged = [...fake_rows];
      for (const [code, data] of Object.entries(current)) {
        merged.push({
          code, region: 'US', name: null,
          is_own: data.is_own,
          owner_tenant_id: data.owner_tenant_id,
          owner_name: data.owner_name,
          centroid_lat: data.lat, centroid_lng: data.lng,
        });
      }
      this.$store.commit('Market_billing/SET_TAKEN_ZIPS', merged);
    },

    /**
     * Admin edit mode: fit the map bounds to the tenant's existing ZIPs
     * so the super-user starts over the right area instead of a country-
     * wide view. Centroids come from Market_billing.taken_zips, which
     * may load after the map is ready — retry a few times if needed.
     */
    frameMapToInitialZips(attempt = 0) {
      if (!this._map || !this.google) return;
      const taken = this.$store.state.Market_billing.taken_zips || {};
      const bounds = new this.google.maps.LatLngBounds();
      let hits = 0;
      for (const zip of this.adminInitialZips) {
        const data = taken[zip];
        if (data && typeof data.lat === 'number' && typeof data.lng === 'number') {
          bounds.extend({ lat: data.lat, lng: data.lng });
          hits++;
        }
      }
      if (hits === 0) {
        // taken_zips not loaded yet — retry up to ~3s of polling.
        if (attempt < 12) {
          setTimeout(() => this.frameMapToInitialZips(attempt + 1), 250);
        }
        return;
      }
      this._map.fitBounds(bounds, 80);
    },

    frameMapToSignupCountry() {
      let country = 'US';
      let pin = null;
      try {
        const raw = localStorage.getItem('signup_address');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.country) country = parsed.country.toUpperCase();
          if (parsed && typeof parsed.lat === 'number' && typeof parsed.lng === 'number') {
            pin = { lat: parsed.lat, lng: parsed.lng };
          }
        }
      } catch (e) { /* fall through to default */ }

      // Mobile viewports are too narrow for desktop zoom levels — at zoom
      // 5, only the Midwest is visible. Drop one level on phones so the
      // whole country fits on first paint.
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 700;
      const COUNTRY_FRAME = {
        US: { center: { lat: 39.5, lng: -98.35 }, zoom: isMobile ? 3 : 5 },
        CA: { center: { lat: 56.13, lng: -106.35 }, zoom: isMobile ? 2 : 4 },
      };
      const frame = COUNTRY_FRAME[country] || COUNTRY_FRAME.US;
      this.map_center = frame.center;
      this.map_zoom = frame.zoom;
      this.office_pin = pin;
    },

    initPlacesAutocomplete() {
      if (!this.google || !this.$refs.placesInput) return;
      const ac = new this.google.maps.places.Autocomplete(this.$refs.placesInput, {
        types: ['geocode'],
        componentRestrictions: { country: ['us'] },
        fields: ['geometry', 'name', 'formatted_address'],
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (!place || !place.geometry) return;
        if (place.geometry.viewport) {
          this._map.fitBounds(place.geometry.viewport);
        } else if (place.geometry.location) {
          this.map_center = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          };
          this.map_zoom = 11;
        }
      });
      this._placesAutocomplete = ac;

      // Google auto-flips the suggestions above the input when it thinks
      // there's not enough space below. Watch the pac-container and force
      // it to stay below our input.
      this.bindPacContainerBelow();
    },

    bindPacContainerBelow() {
      const wrapper = this.$refs.placesInput && this.$refs.placesInput.parentElement;
      if (!wrapper) return;
      const adopt = () => {
        const candidates = document.querySelectorAll('.pac-container');
        if (!candidates.length) return false;
        const pac = candidates[candidates.length - 1];
        if (pac._kieyAdopted) return true;
        pac._kieyAdopted = true;
        pac.classList.add('kiey-pac-below');
        // Move the dropdown inside our relatively-positioned search-wrapper so
        // Google's inline top/left/width are overridden by our CSS below.
        wrapper.appendChild(pac);
        return true;
      };
      if (adopt()) return;
      this._pacBodyObserver = new MutationObserver(() => { if (adopt()) this._pacBodyObserver.disconnect(); });
      this._pacBodyObserver.observe(document.body, { childList: true });
      // Safety net: if Places never creates a pac-container, disconnect after
      // 30s so we don't leak the observer for the page lifetime.
      this._pacObserverTimeout = setTimeout(() => {
        if (this._pacBodyObserver) {
          this._pacBodyObserver.disconnect();
          this._pacBodyObserver = null;
        }
      }, 30000);
    },

    /* ================= DRAWING LIFECYCLE ================= */

    startDrawing() {
      if (!this._map) {
        this.error_message = 'Map is still loading. Please wait a moment and try again.';
        return;
      }
      if (!this.google) {
        this.error_message = 'Google Maps is still loading. Please wait a moment.';
        return;
      }
      // If the user arrived via a shared link, ditch the teal preview before
      // they start drawing their own — two polygons on the map is confusing.
      if (this._sharedPolygon) this.clearSharedPreview();
      this.clearPolygon();
      this.drawing_mode = 'drawing';
      this._map.setOptions({ draggableCursor: 'crosshair' });

      this._polyline = new this.google.maps.Polyline({
        path: [],
        strokeColor: '#061D47',
        strokeOpacity: 0.9,
        strokeWeight: 3,
        clickable: false,
        map: this._map,
      });

      this._drawingClickListener = this._map.addListener('click', (e) => {
        if (!e || !e.latLng) return;
        this.addVertex(e.latLng);
      });
    },

    addVertex(latLng) {
      if (!this._polyline) return;
      const path = this._polyline.getPath();

      // Block edges that would cross a reserved territory (even if both
      // endpoints are outside it). Sampled-line check against every hull.
      if (path.getLength() > 0) {
        const prev = path.getAt(path.getLength() - 1);
        const crossedOwner = this.segmentCrossesTaken(prev, latLng);
        if (crossedOwner) {
          this.flashTakenError(`Your line would cross ${crossedOwner}'s market.`);
          return;
        }
        // Same rule for the admin's own committed areas — areas can't overlap.
        if (this.segmentCrossesCommittedArea(prev, latLng)) {
          this.flashTakenError('Your line would cross another of your areas.');
          return;
        }
      }

      path.push(latLng);
      this.polygon_point_count = path.getLength();

      const marker = new this.google.maps.Marker({
        position: latLng,
        map: this._map,
        icon: this.vertexIcon(),
        clickable: false,
        zIndex: 10,
      });
      this._vertexMarkers.push(marker);

      // Live population update once the user has enough points for a real shape.
      // Scheduled after paint so the marker renders instantly; debounced so
      // rapid clicks don't pile up 33k-ZIP scans.
      if (this.polygon_point_count >= 3) this.scheduleRecompute(path);
    },

    undoVertex() {
      if (!this._polyline) return;
      const path = this._polyline.getPath();
      if (path.getLength() === 0) return;
      path.pop();
      const marker = this._vertexMarkers.pop();
      if (marker) marker.setMap(null);
      this.polygon_point_count = path.getLength();

      if (this.polygon_point_count >= 3) {
        this.scheduleRecompute(path);
      } else {
        // Not enough points anymore; cancel any pending recompute and clear
        // the live polygon's contribution. commitLiveSelection re-merges
        // committed areas (admin Edit); in every other mode it's set_selection([]).
        this.cancelPendingRecompute();
        this.live_zips = [];
        this.commitLiveSelection([]);
        this.excluded_count = 0;
        this.selection_radius_miles = 0;
        this.overlap_owner_ids = {};
        this.self_overlap = false;
      }
    },

    finishDrawing() {
      if (this.polygon_point_count < 3 || !this._polyline || !this._map) return;
      const path = this._polyline.getPath();

      // Closing edge (last → first) must also avoid reserved territory.
      const last = path.getAt(path.getLength() - 1);
      const first = path.getAt(0);
      const crossedOwner = this.segmentCrossesTaken(last, first);
      if (crossedOwner) {
        this.flashTakenError(`Closing the shape would cross ${crossedOwner}'s market. Add another point first.`);
        return;
      }
      if (this.segmentCrossesCommittedArea(last, first)) {
        this.flashTakenError('Closing the shape would cross another of your areas. Add another point first.');
        return;
      }

      const points = [];
      for (let i = 0; i < path.getLength(); i++) {
        const p = path.getAt(i);
        points.push({ lat: p.lat(), lng: p.lng() });
      }

      // Tear down the in-progress drawing overlays
      this._polyline.setMap(null);
      this._polyline = null;
      for (const m of this._vertexMarkers) m.setMap(null);
      this._vertexMarkers = [];
      if (this._drawingClickListener) {
        this.google.maps.event.removeListener(this._drawingClickListener);
        this._drawingClickListener = null;
      }
      this._map.setOptions({ draggableCursor: null });

      this.createEditablePolygon(points);
      this.attachPolygonListeners();
      this.drawing_mode = 'done';
      this.recomputeSelection();
    },

    // Create the live editable polygon from a points array and assign it to
    // _polygon. Vertices are draggable; midpoints appear as lighter handles
    // users can drag to add new corners in-between. Shared by finishDrawing
    // (a freshly drawn shape) and editCommittedArea (a promoted committed area).
    createEditablePolygon(points) {
      this._polygon = new this.google.maps.Polygon({
        paths: points,
        strokeColor: '#061D47',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: '#061D47',
        fillOpacity: 0.14,
        editable: true,
        draggable: true,
        zIndex: 2,
        map: this._map,
      });
    },

    attachPolygonListeners() {
      if (!this._polygon) return;
      const path = this._polygon.getPath();
      const debouncedRecompute = this.debounce(() => this.recomputeSelection(), 250);

      this._polygonListeners.push(path.addListener('set_at', debouncedRecompute));
      this._polygonListeners.push(path.addListener('insert_at', debouncedRecompute));
      this._polygonListeners.push(path.addListener('remove_at', debouncedRecompute));
      this._polygonListeners.push(this._polygon.addListener('dragend', debouncedRecompute));
    },

    // Exit drawing mode without finalizing. Removes the in-progress polyline
    // and vertex markers, restores the normal cursor, clears the readout.
    cancelDrawing() {
      if (this.drawing_mode !== 'drawing') return;
      this.clearPolygon();
    },

    clearPolygon() {
      // Remove everything and reset state
      if (this._polyline) {
        this._polyline.setMap(null);
        this._polyline = null;
      }
      for (const m of this._vertexMarkers) m.setMap(null);
      this._vertexMarkers = [];
      if (this._drawingClickListener && this.google) {
        this.google.maps.event.removeListener(this._drawingClickListener);
        this._drawingClickListener = null;
      }
      if (this._polygon) {
        this._polygon.setMap(null);
        this._polygon = null;
      }
      if (this.google) {
        for (const l of this._polygonListeners) this.google.maps.event.removeListener(l);
      }
      this._polygonListeners = [];
      if (this._map) this._map.setOptions({ draggableCursor: null });
      this.cancelPendingRecompute();
      this.polygon_point_count = 0;
      this.drawing_mode = 'initial';
      this.error_message = '';
      this.excluded_count = 0;
      this.selection_radius_miles = 0;
      this.overlap_owner_ids = {};
      this.self_overlap = false;
      this._scanToken = (this._scanToken || 0) + 1; // abort any in-flight scan
      // Clears the LIVE polygon only — committed areas (admin multi-area)
      // survive. commitLiveSelection re-merges them; in public mode
      // committed_areas is empty so this is just set_selection([]).
      this.live_zips = [];
      this.commitLiveSelection([]);
    },

    /* ============== ADMIN MULTI-AREA ============== */

    // Union committed-area ZIPs with the live polygon's candidates, dedupe,
    // and push the merged selection to the store. In public mode
    // committed_areas is always empty so this equals set_selection(live).
    // In admin Edit mode the edited tenant's own ZIPs read back as taken
    // (is_own is relative to the super-user), so pass edit_tenant_id through
    // to set_selection or they'd be filtered out of the selection.
    commitLiveSelection(liveCandidates) {
      const union = new Set();
      for (const area of this.committed_areas) {
        for (const z of area.zips) union.add(z);
      }
      for (const z of (liveCandidates || [])) union.add(z);
      const zips = Array.from(union);
      if (this.adminEditTenantId) {
        return this.set_selection({ zips, edit_tenant_id: this.adminEditTenantId });
      }
      return this.set_selection(zips);
    },

    // Freeze the live editable polygon into a committed area: snapshot its
    // ZIPs + hull, draw a static outline + delete badge, tear down the live
    // polygon. Admin multi-area only; no-op without a valid live polygon.
    commitCurrentArea() {
      if (!this._polygon || !this.google) return;
      const path = this._polygon.getPath();
      const hull = [];
      for (let i = 0; i < path.getLength(); i++) {
        const p = path.getAt(i);
        hull.push({ lat: p.lat(), lng: p.lng() });
      }
      if (hull.length < 3) return;
      this.committed_areas.push({ zips: [...this.live_zips], hull });
      this.drawCommittedOverlay(this.committed_areas.length - 1);
      if (this.google) {
        for (const l of this._polygonListeners) this.google.maps.event.removeListener(l);
      }
      this._polygonListeners = [];
      this._polygon.setMap(null);
      this._polygon = null;
      this.live_zips = [];
      this.polygon_point_count = 0;
      this.selection_radius_miles = 0;
      this.overlap_owner_ids = {};
      this.self_overlap = false;
      this.drawing_mode = 'initial';
    },

    // Draw the static (non-editable) outline + a clickable delete badge for
    // the committed area at `index`. Stored in _committedOverlays by index.
    drawCommittedOverlay(index) {
      if (!this._map || !this.google) return;
      const area = this.committed_areas[index];
      if (!area) return;
      const outline = new this.google.maps.Polygon({
        paths: area.hull,
        strokeColor: '#061D47',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: '#061D47',
        fillOpacity: 0.10,
        clickable: false,
        editable: false,
        draggable: false,
        zIndex: 1,
        map: this._map,
      });
      const centroid = this.hullCentroid(area.hull);
      const badge = new this.google.maps.Marker({
        position: centroid,
        map: this._map,
        icon: this.areaBadgeIcon(),
        label: { text: '×', color: '#ffffff', fontSize: '16px', fontWeight: 'bold' },
        zIndex: 11,
        cursor: 'pointer',
      });
      badge.addListener('click', () => this.removeCommittedArea(badge));
      // ✎ edit badge sits just to the right of the × badge. The offset
      // scales with the area's width so the two badges stay visually
      // separated at any zoom.
      let lngReach = 0;
      for (const p of area.hull) {
        lngReach = Math.max(lngReach, Math.abs(p.lng - centroid.lng));
      }
      const editBadge = new this.google.maps.Marker({
        position: { lat: centroid.lat, lng: centroid.lng + lngReach * 0.35 },
        map: this._map,
        icon: this.areaEditBadgeIcon(),
        label: { text: '✎', color: '#ffffff', fontSize: '14px', fontWeight: 'bold' },
        zIndex: 11,
        cursor: 'pointer',
      });
      editBadge.addListener('click', () => this.editCommittedArea(editBadge));
      this._committedOverlays[index] = { outline, badge, editBadge };
    },

    // Delete the committed area whose badge marker was clicked. Resolves by
    // marker reference (not index) since splices shift indices.
    removeCommittedArea(badge) {
      const idx = this._committedOverlays.findIndex((ov) => ov.badge === badge);
      if (idx === -1) return;
      const ov = this._committedOverlays[idx];
      if (ov.outline) ov.outline.setMap(null);
      if (ov.badge) ov.badge.setMap(null);
      if (ov.editBadge) ov.editBadge.setMap(null);
      this._committedOverlays.splice(idx, 1);
      this.committed_areas.splice(idx, 1);
      this.commitLiveSelection(this.live_zips);
    },

    // Admin "+ Add Another Area": freeze whatever's live, then draw fresh.
    startAdditionalArea() {
      if (this.drawing_mode === 'done') this.commitCurrentArea();
      this.startDrawing();
    },

    // Promote a committed area back to the live editable polygon so the
    // super-user can reshape it. Resolves the target by its ✎ edit-badge
    // reference. Whatever is currently live is committed first so it isn't
    // lost — same pattern as startAdditionalArea.
    editCommittedArea(editBadge) {
      // Don't hijack an in-progress draw.
      if (this.drawing_mode === 'drawing') return;
      if (this.drawing_mode === 'done') this.commitCurrentArea();
      // commitCurrentArea only appends, so the target's index is unshifted.
      const idx = this._committedOverlays.findIndex((ov) => ov.editBadge === editBadge);
      if (idx === -1) return;
      const ov = this._committedOverlays[idx];
      const area = this.committed_areas[idx];
      if (!area || !Array.isArray(area.hull) || area.hull.length < 3) return;
      // Tear down the frozen overlay for this area.
      if (ov.outline) ov.outline.setMap(null);
      if (ov.badge) ov.badge.setMap(null);
      if (ov.editBadge) ov.editBadge.setMap(null);
      this._committedOverlays.splice(idx, 1);
      this.committed_areas.splice(idx, 1);
      // Promote its hull to the live editable polygon.
      this.createEditablePolygon(area.hull.map((p) => ({ lat: p.lat, lng: p.lng })));
      this.attachPolygonListeners();
      this.drawing_mode = 'done';
      this.recomputeSelection();
    },

    // Admin "Start Over": drop every committed area + the live polygon.
    clearAllAreas() {
      for (const ov of this._committedOverlays) {
        if (ov.outline) ov.outline.setMap(null);
        if (ov.badge) ov.badge.setMap(null);
        if (ov.editBadge) ov.editBadge.setMap(null);
      }
      this._committedOverlays = [];
      this.committed_areas = [];
      this.clearPolygon();
    },

    areaBadgeIcon() {
      return {
        path: this.google.maps.SymbolPath.CIRCLE,
        scale: 11,
        fillColor: '#B71C1C',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      };
    },

    // Edit badge — Kiey-primary blue so the ✎ reads distinctly from the
    // red × delete badge.
    areaEditBadgeIcon() {
      return {
        path: this.google.maps.SymbolPath.CIRCLE,
        scale: 11,
        fillColor: '#061D47',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      };
    },

    // Admin Edit mode: hydrate the edited tenant's existing market as
    // committed areas. The per-area hulls are already grouped by the
    // taken_markets_by_owner getter (keyed by owner + area_index). taken_zips
    // loads async, so retry-poll until it's populated, then hydrate once.
    hydrateAdminAreas(attempt = 0) {
      if (this._adminAreasHydrated) return;
      if (!this.adminEditTenantId || !this._map || !this.google) return;
      const takenLoaded = Object.keys(
        this.$store.state.Market_billing.taken_zips || {},
      ).length > 0;
      if (!takenLoaded) {
        if (attempt < 12) {
          setTimeout(() => this.hydrateAdminAreas(attempt + 1), 250);
        }
        return;
      }
      this._adminAreasHydrated = true;
      const byKey = this.taken_markets_by_owner || {};
      const mine = Object.keys(byKey)
        .map((k) => byKey[k])
        .filter((m) => m.owner_tenant_id === this.adminEditTenantId);
      for (const m of mine) {
        if (!Array.isArray(m.hull) || m.hull.length < 3) continue;
        this.committed_areas.push({
          zips: [...(m.zips || [])],
          hull: m.hull.map((p) => ({ lat: p.lat, lng: p.lng })),
        });
        this.drawCommittedOverlay(this.committed_areas.length - 1);
      }
      this.commitLiveSelection([]);
    },

    teardownAllOverlays() {
      if (this._polyline) this._polyline.setMap(null);
      if (this._polygon) this._polygon.setMap(null);
      for (const m of this._vertexMarkers) m.setMap(null);
      this._vertexMarkers = [];
      for (const ov of this._committedOverlays) {
        if (ov.outline) ov.outline.setMap(null);
        if (ov.badge) ov.badge.setMap(null);
        if (ov.editBadge) ov.editBadge.setMap(null);
      }
      this._committedOverlays = [];
    },

    /* ================= ZIP COMPUTE ================= */

    recomputeSelection() {
      if (!this._polygon) return;
      this.recomputeFromPath(this._polygon.getPath(), this._polygon);
    },

    // Queue a recompute for the next animation frame, debounced so rapid
    // clicks/edits collapse into one pass. Paint-first = instant marker.
    scheduleRecompute(path, existingPolygon = null) {
      this.cancelPendingRecompute();
      this._pendingRecomputeRAF = requestAnimationFrame(() => {
        this._pendingRecomputeRAF = null;
        this._pendingRecomputeId = setTimeout(() => {
          this._pendingRecomputeId = null;
          this.recomputeFromPath(path, existingPolygon);
        }, 120);
      });
    },

    cancelPendingRecompute() {
      if (this._pendingRecomputeRAF) {
        cancelAnimationFrame(this._pendingRecomputeRAF);
        this._pendingRecomputeRAF = null;
      }
      if (this._pendingRecomputeId) {
        clearTimeout(this._pendingRecomputeId);
        this._pendingRecomputeId = null;
      }
    },

    /**
     * Compute ZIPs inside the given path and commit them to the selection.
     * Used both during drawing (from the in-progress polyline) and after the
     * polygon is finalized (from the editable polygon).
     *
     * Yields to the browser every CHUNK_SIZE entries so large polygons don't
     * freeze the main thread during vertex drags.
     *
     * When called during drawing, we build a throwaway Polygon for the
     * containsLocation test — it is never added to the map.
     */
    recomputeFromPath(path, existingPolygon = null) {
      if (!this.zip_data || !this.zip_data.data) return;
      if (!this.google || !this.google.maps.geometry) return;
      if (!path || path.getLength() < 3) return;

      // Radius is cheap — compute it up front so the readout is instant even
      // while the ZIP scan is still running.
      const areaM2 = this.google.maps.geometry.spherical.computeArea(path);
      const radiusMeters = Math.sqrt(areaM2 / Math.PI);
      this.selection_radius_miles = radiusMeters / 1609.344;

      // Bounding box prefilter — huge perf win over testing every ZIP in the US
      const bounds = new this.google.maps.LatLngBounds();
      for (let i = 0; i < path.getLength(); i++) bounds.extend(path.getAt(i));
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      const swLat = sw.lat(), neLat = ne.lat(), swLng = sw.lng(), neLng = ne.lng();

      const testPolygon = existingPolygon || new this.google.maps.Polygon({ paths: path });
      const contains = this.google.maps.geometry.poly.containsLocation;
      const data = this.zip_data.data;
      const takenZips = this.$store.state.Market_billing.taken_zips;
      const candidates = [];
      const overlapOwners = {};
      // Self-overlap: ZIPs already claimed by one of the admin's committed
      // areas. Mirrors the taken-by-other-tenant check inside the scan.
      const committedZipSet = new Set();
      for (const area of this.committed_areas) {
        for (const z of area.zips) committedZipSet.add(z);
      }
      let selfOverlap = false;

      // Bump a scan token so a late-arriving chunk from a previous scan can
      // abort itself if the user kept editing.
      const token = (this._scanToken = (this._scanToken || 0) + 1);
      // Small chunks + setTimeout(0) yields back to the browser fast so an
      // incoming click isn't queued behind the scan.
      const CHUNK_SIZE = 500;
      const yielder = (cb) => setTimeout(cb, 0);

      const scanChunk = (start) => {
        if (token !== this._scanToken) return; // superseded — bail
        const end = Math.min(start + CHUNK_SIZE, data.length);
        for (let i = start; i < end; i++) {
          const entry = data[i];
          if (entry.la < swLat || entry.la > neLat) continue;
          if (entry.ln < swLng || entry.ln > neLng) continue;
          const latLng = new this.google.maps.LatLng(entry.la, entry.ln);
          if (!contains(latLng, testPolygon)) continue;
          candidates.push(entry.z);
          if (committedZipSet.has(entry.z)) selfOverlap = true;
          const taken = takenZips[entry.z];
          // Admin edit mode: ZIPs already owned by the tenant being edited
          // count as available (we're going to overwrite them anyway).
          const isOwnEdit = taken && this.adminEditTenantId && taken.owner_tenant_id === this.adminEditTenantId;
          if (taken && !taken.is_own && !isOwnEdit) {
            const ownerId = taken.owner_tenant_id || taken.owner_name || 'unknown';
            overlapOwners[ownerId] = taken.owner_name || 'Another agent';
          }
        }
        if (end < data.length) {
          yielder(() => scanChunk(end));
          return;
        }
        // Done — commit results. live_zips is the live polygon's ZIP set;
        // commitLiveSelection unions it with any committed areas (admin
        // multi-area). In public mode committed_areas is empty so this is
        // equivalent to the old set_selection(candidates).
        this.overlap_owner_ids = overlapOwners;
        this.self_overlap = selfOverlap;
        this.live_zips = candidates;
        this.commitLiveSelection(candidates).then((result) => {
          if (token !== this._scanToken) return;
          this.excluded_count = result.skipped_taken.length;
        });
      };
      scanChunk(0);
    },

    /* ================= ICONS + UTIL ================= */

    vertexIcon() {
      return {
        path: this.google.maps.SymbolPath.CIRCLE,
        scale: 6,
        fillColor: '#061D47',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      };
    },

    debounce(fn, delay) {
      let t = null;
      return (...args) => {
        if (t) clearTimeout(t);
        t = setTimeout(() => fn(...args), delay);
      };
    },

    /* ================= CHECKOUT + NAV ================= */

    // Routed by the bottom CTA. Three paths:
    //   - authenticated signup → Stripe claim+checkout
    //   - public + viewing a shared market (no drawing yet) → clear preview
    //     and start drawing their own
    //   - public + finished drawing their own → sign up to claim
    /**
     * Cancel button (admin mode). Emits to a Vue parent when embedded as a
     * child component, OR postMessages to window.parent when this page is
     * loaded inside the super-admin iframe.
     */
    onAdminCancel() {
      this.$emit('cancel');
      if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
        try {
          window.parent.postMessage(
            { type: 'super-admin-market-cancel' },
            window.location.origin,
          );
        } catch (e) { /* ignore */ }
      }
    },

    onPrimaryCTA() {
      if (this.isAdmin) {
        // The big Done CTA does double duty: if the user is still mid-draw
        // with 3+ points, finalize the polygon first (which populates
        // selected_zips). Otherwise the parent would get zips=[].
        if (this.drawing_mode === 'drawing' && this.polygon_point_count >= 3) {
          this.finishDrawing();
          if (this.drawing_mode !== 'done') return;
        }
        let payload;
        if (this.isAdminEdit) {
          // Multi-area: commit the live polygon, then submit committed_areas.
          // areas[] mirrors committed_areas; zips is the deduped union the
          // backend uses as the authoritative claim set.
          if (this.drawing_mode === 'done') this.commitCurrentArea();
          if (!this.committed_areas.length || !this.selected_zips.length) return;
          payload = {
            zips: [...this.selected_zips],
            areas: this.committed_areas.map((a) => [...a.zips]),
            population: this.selected_population || 0,
            calculated: this.market_price || 0,
            tenant_id: this.adminTenantId,
          };
        } else {
          // Admin grant: single-polygon, no areas[] — unchanged behavior.
          if (!this.selected_zips.length) return;
          payload = {
            zips: [...this.selected_zips],
            population: this.selected_population || 0,
            calculated: this.market_price || 0,
            tenant_id: this.adminTenantId,
          };
        }
        // Inline-embed path: emit Vue event up to the parent component.
        this.$emit('done', payload);
        // iframe-embed path: post the same payload to the parent window
        // so the super-admin Markets surface can hand it to the confirm
        // dialog. Same-origin only (always our own host) so origin matches
        // window.location.origin.
        if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
          try {
            window.parent.postMessage({
              type: 'super-admin-market-done',
              ...payload,
            }, window.location.origin);
          } catch (e) { /* ignore — postMessage failures shouldn't block */ }
        }
        return;
      }
      if (this.isPublic) {
        if (this.has_shared_preview && this.drawing_mode === 'initial') {
          this.clearSharedPreview();
          this.startDrawing();
          return;
        }
        // /my-market is served from multiple domains: kiey.com (marketing
        // subpath), admin.kiey.com prod, kiey-test-70e4a staging, and
        // localhost in dev. /sign_up only exists under admin (or at
        // dev-time, localhost), so we navigate cross-origin — Vue Router
        // push would land at kiey.com/sign_up which 404s.
        //
        // On localhost we keep the user on the same origin so the full
        // /my-market → /sign_up flow can be tested end-to-end without
        // bouncing out to staging Firebase. Otherwise the handoff goes
        // to VUE_APP_HOST_URL (admin.kiey.com or staging). ZIPs +
        // pricing_mode ride along on the URL so the wizard's pricing
        // breakdown reflects the claimed territory.
        const zips = Array.isArray(this.selected_zips) ? this.selected_zips : [];
        const isLocalhost =
          typeof window !== 'undefined' && window.location.hostname === 'localhost';
        const host = isLocalhost
          ? window.location.origin + '/'
          : (process.env.VUE_APP_HOST_URL || 'https://admin.kiey.com/');
        let target = host + 'sign_up';
        if (zips.length > 0) {
          const params = new URLSearchParams();
          params.set('zips', zips.join(','));
          params.set('pricing_mode', 'calculated');
          target += '?' + params.toString();
        }
        window.location.href = target;
        return;
      }
      return this.proceedToCheckout();
    },

    async proceedToCheckout() {
      this.error_message = "";
      const result = await this.checkout();
      if (result.success && result.url) {
        window.location.href = result.url;
      } else if (result.error) {
        this.error_message = result.message;
      }
    },

    /* ================= SHARE (public mode) ================= */

    // Save and Share button. If the polygon is still being drawn, finalize
    // it first so the link we're about to hand out represents a valid,
    // closed market. Then open the share popover.
    saveAndShare() {
      if (this.drawing_mode === 'drawing') {
        if (this.polygon_point_count < 3) return;
        this.finishDrawing();
        // finishDrawing bails (and stays in 'drawing' mode) if the closing
        // edge crosses a reserved territory — don't open the menu in that
        // case, the user sees the error banner instead.
        if (this.drawing_mode !== 'done') return;
      }
      this.share_open = true;
    },

    shareVia(channel) {
      const msgs = this.share_messages;
      switch (channel) {
        case 'email': {
          const subject = encodeURIComponent(msgs.email.subject);
          const body = encodeURIComponent(msgs.email.body);
          window.location.href = `mailto:?subject=${subject}&body=${body}`;
          return;
        }
        case 'copy':
          if (navigator && navigator.clipboard) {
            navigator.clipboard.writeText(this.share_url).then(() => {
              this.copied = true;
              setTimeout(() => { this.copied = false; }, 1800);
            });
          }
          return;
        case 'reserve': {
          // Soft-lead path: user drew their market but isn't ready to sign
          // up yet. Opens a pre-filled email to Kiey with enough detail that
          // the team can hold the market for them and follow up.
          const pop = this.effective_population || 0;
          // Unlike market_price (which shows "—" for an undrawn market),
          // the lead email always quotes a concrete number, so fall back to
          // the floor as the starting price.
          const price = floored_market_price(pop) || MARKET_PRICE_FLOOR;
          const radius = this.selection_radius_miles
            ? `${this.selection_radius_miles.toFixed(this.selection_radius_miles < 10 ? 1 : 0)} mi`
            : '—';
          const subject = encodeURIComponent('Reserve my market on Kiey');
          const body = encodeURIComponent(
            "Hi Kiey,\n\n" +
            "I'd like to reserve this market:\n\n" +
            `  Monthly price:  $${price.toLocaleString('en-US')}\n` +
            `  Population:     ~${pop.toLocaleString('en-US')} residents\n` +
            `  Radius:         ~${radius}\n` +
            `  Map:            ${this.share_url}\n\n` +
            "Can you hold this market for 48 hours?\n\n" +
            "Thanks!",
          );
          window.location.href = `mailto:support@kiey.com?subject=${subject}&body=${body}`;
          return;
        }
        default:
          return;
      }
    },

    signOut() {
      this.$store.dispatch('Authenticate/logout');
      this.$router.push('/');
    },

    /* ================= SHARED MARKET PREVIEW (?m= ...) ================= */

    // Called on mount if the URL carries a ?m= param. Decodes the polygon,
    // draws it as a read-only teal preview, and computes population so the
    // price block shows the sender's price at a glance.
    loadSharedMarket(encoded) {
      try {
        const b64 = decodeURIComponent(encoded);
        const json = decodeURIComponent(escape(atob(b64)));
        const payload = JSON.parse(json);
        if (!payload || !Array.isArray(payload.p) || payload.p.length < 3) return;
        const vertices = payload.p.map(([lat, lng]) => ({ lat: +lat, lng: +lng }));
        this.shared_market_preview = { vertices, population: 0 };
        this.drawSharedPreview(this._map, vertices);
        this.computeSharedPopulation(vertices);
      } catch (e) {
        console.warn('[market_selection] invalid shared market payload:', e);
        this.shared_market_preview = null;
      }
    },

    drawSharedPreview(map, vertices) {
      if (!this.google || !map) return;
      if (this._sharedPolygon) this._sharedPolygon.setMap(null);
      this._sharedPolygon = new this.google.maps.Polygon({
        paths: vertices,
        strokeColor: '#19A99D',
        strokeOpacity: 0.95,
        strokeWeight: 3,
        fillColor: '#19A99D',
        fillOpacity: 0.22,
        editable: false,
        clickable: false,
        zIndex: 3,
        map,
      });
      const bounds = new this.google.maps.LatLngBounds();
      for (const v of vertices) bounds.extend(v);
      map.fitBounds(bounds, 80);

      // Equivalent-circle radius (same math as the user's own drawing) so the
      // readout can display it alongside the sender's price.
      if (this.google.maps.geometry) {
        const path = this._sharedPolygon.getPath();
        const areaM2 = this.google.maps.geometry.spherical.computeArea(path);
        const radiusMeters = Math.sqrt(areaM2 / Math.PI);
        this.selection_radius_miles = radiusMeters / 1609.344;
      }
    },

    // Same point-in-polygon scan as recomputeFromPath, one-shot and unchunked
    // (preview polygons are small enough that chunking isn't worth it).
    computeSharedPopulation(vertices) {
      const run = () => {
        if (!this.zip_data || !this.zip_data.data) return;
        if (!this.google || !this.google.maps.geometry) return;
        const poly = new this.google.maps.Polygon({ paths: vertices });
        const contains = this.google.maps.geometry.poly.containsLocation;
        const bounds = new this.google.maps.LatLngBounds();
        for (const v of vertices) bounds.extend(v);
        const sw = bounds.getSouthWest(), ne = bounds.getNorthEast();
        const swLat = sw.lat(), neLat = ne.lat(), swLng = sw.lng(), neLng = ne.lng();
        const data = this.zip_data.data;
        let total = 0;
        for (let i = 0; i < data.length; i++) {
          const e = data[i];
          if (e.la < swLat || e.la > neLat) continue;
          if (e.ln < swLng || e.ln > neLng) continue;
          if (contains(new this.google.maps.LatLng(e.la, e.ln), poly)) total += e.p || 0;
        }
        if (this.shared_market_preview) {
          this.shared_market_preview = { ...this.shared_market_preview, population: total };
        }
      };
      if (this.zip_data && this.zip_data.data) {
        run();
      } else {
        const unwatch = this.$watch('zip_data', () => {
          run();
          unwatch();
        });
      }
    },

    clearSharedPreview() {
      if (this._sharedPolygon) {
        this._sharedPolygon.setMap(null);
        this._sharedPolygon = null;
      }
      this.shared_market_preview = null;
    },
  },
};
</script>

<style scoped>
/* Stepper */
.stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin: 0 0 6px;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 70px;
}

.step-circle {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #e4e8f1;
  color: #9aa2b4;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  transition: background 0.15s, color 0.15s;
}

.step-active .step-circle {
  background: #061D47;
  color: #fff;
}

.step-done .step-circle {
  background: #19A99D;
  color: #fff;
  cursor: pointer;
}

.step-label {
  font-size: 10px;
  color: #9aa2b4;
  margin-top: 2px;
  letter-spacing: 0.3px;
  font-weight: 600;
  text-transform: uppercase;
}

.step-active .step-label {
  color: #061D47;
}

.step-done .step-label {
  color: #19A99D;
  cursor: pointer;
}

.step-line {
  flex: 0 1 80px;
  height: 2px;
  background: #e4e8f1;
  margin: 0 4px 12px;
}

.step-line-done {
  background: #19A99D;
}

/* Header */
.title_agent {
  color: #041332;
  font-family: "Roboto-Medium";
  font-size: 22px;
  font-weight: 500;
  line-height: 28px;
  margin-bottom: 4px;
}

.market-subtitle {
  color: #5d5d5d;
  font-size: 13px;
  margin: 0;
  line-height: 1.4;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  padding: 0 4px;
}

.price-block {
  text-align: right;
  flex-shrink: 0;
}

.price-amount {
  color: #061D47;
  font-size: 24px;
  font-weight: 700;
}

.price-period {
  color: #5d5d5d;
  font-size: 16px;
}

.price-note {
  color: #5d5d5d;
  font-size: 12px;
  font-weight: 700;
  margin: 0;
}

/* Top controls row */
.top-controls {
  display: flex;
  gap: 12px;
  align-items: center;
}

.search-wrapper {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9a9a9a;
  font-size: 14px;
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 40px;
  padding: 0 12px 0 32px;
  border: 1px solid #d7d7d7;
  border-radius: 20px;
  font-size: 14px;
  background: #fff;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.search-input:focus {
  border-color: #061D47;
  box-shadow: 0 0 0 3px rgba(6, 29, 71, 0.1);
}

.draw-buttons {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.draw-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
}

/* Map */
.map-container {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
}

.map-container.has-readout {
  border-radius: 10px 10px 0 0;
  border-bottom: none;
}

.market-map {
  width: 100%;
  height: 100%;
}

/* "What you get" value framing — public /my-market quote tool only.
   Compact two-column grid on desktop so it sits between the price and
   the map without devouring map space; collapses to a single-column
   stack on phones. Subtle card surface (matches the existing readout
   bar) keeps it clearly secondary to the map and the Claim CTA. */
.my-market-includes {
  background: #f5f7fb;
  border: 1px solid #e4e8f1;
  border-radius: 10px;
  padding: 10px 14px 12px;
  margin: 0 0 10px;
}
.my-market-includes-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #061D47;
  margin: 0 0 8px;
}
.my-market-includes-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 18px;
  row-gap: 4px;
}
.my-market-includes-list li {
  position: relative;
  padding: 2px 0 2px 22px;
  color: #2d2d2d;
  font-size: 13px;
  line-height: 1.5;
}
.my-market-includes-list li::before {
  content: '\2713';
  position: absolute;
  left: 0;
  top: 4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #29abe2;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.my-market-includes-list li strong {
  color: #061D47;
  font-weight: 700;
}

/* Territory disclaimer — shown on both /select_market and /my-market. */
.territory-disclaimer {
  font-size: 11px;
  color: #666;
  margin: 6px 2px 0;
  display: flex;
  align-items: center;
  line-height: 1.4;
}

/* Cancel/Undo during drawing — upgraded from small text to proper outlined
   buttons since they're now the only things in the top-right. */
.draw-btn-lg {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
}

/* "Email Kiey to Reserve" — the primary warm-lead action inside the share
   popover. Keep it bold and visually separated from the social share row. */
.reserve-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-size: 14px !important;
  height: 44px !important;
}
.reserve-btn strong {
  font-weight: 700;
}

/* Divider under the Reserve CTA ("or share it") */
.share-divider {
  display: flex;
  align-items: center;
  margin: 6px 0 12px;
  color: #9aa2b4;
  font-size: 11px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  font-weight: 600;
}
.share-divider::before,
.share-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e4e8f1;
}
.share-divider span {
  padding: 0 10px;
}
.share-title {
  color: #061D47;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 6px;
}
.share-body {
  color: #5d5d5d;
  font-size: 14px;
  margin-bottom: 18px;
}
.share-actions .v-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
}

/* Legend — inline row in the actions bar */
.map-legend {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  color: #555;
  margin-right: auto;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.legend-swatch {
  width: 12px;
  height: 12px;
  display: inline-block;
  border-radius: 50%;
  background: #B71C1C;
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 1px #B71C1C;
  flex-shrink: 0;
}
.legend-office {
  background: #D0021B;
  box-shadow: 0 0 0 1px #D0021B;
}
.legend-yours {
  background: #061D47;
  box-shadow: 0 0 0 1px #061D47;
}

/* Readout */
.readout {
  padding: 10px 14px;
  margin: 0;
  background: #f5f7fb;
  border: 1px solid #e4e8f1;
  border-top: none;
  border-radius: 0 0 10px 10px;
}

.readout-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.readout-line {
  font-size: 15px;
  color: #061D47;
}

.readout-sep {
  color: #b0b7c3;
  margin: 0 8px;
}

/* Done button on the readout bar. Small teal pill with white text so it
   stays clearly actionable no matter what map tile is behind the bar.
   Chevron reinforces "next step" semantics. */
.readout-done-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #19A99D;
  border: none;
  padding: 6px 14px;
  border-radius: 999px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: #fff;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
  box-shadow: 0 2px 6px -2px rgba(25, 169, 157, 0.4);
}
.readout-done-btn:not(:disabled):hover {
  background: #158a80;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px -2px rgba(25, 169, 157, 0.5);
}
.readout-done-btn:disabled {
  background: #c9cdd6;
  color: #fff;
  cursor: not-allowed;
  box-shadow: none;
}
/* Finalized state — drawing_mode flipped to 'done'. Slightly more vivid
   than the plain disabled grey so the user can tell their click landed,
   while still being clearly inactive (next action is Confirm below). */
.readout-done-btn.is-finalized:disabled {
  background: #e6efee;
  color: #0B5D55;
}
.readout-done-arrow {
  font-weight: 700;
  display: inline-block;
  transform: translateY(-0.5px);
}

.readout-muted {
  color: #5d5d5d;
  font-size: 13px;
}

.readout-note {
  font-size: 12px;
  color: #777;
  margin-top: 4px;
}

/* Admin Edit: per-area resident + ZIP breakdown, listed under the main
   readout line. Indented + slightly muted so it reads as detail below
   the headline Coverage total. */
.readout-areas {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #d8deea;
}
.readout-area-line {
  font-size: 13px;
  color: #4a5468;
  padding: 1px 0;
}
.readout-area-line strong {
  color: #061D47;
}

/* Actions */
.actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 4px 12px 0;
  margin-top: 8px;
  flex-wrap: wrap;
}

.continue-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  padding: 0 32px !important;
  min-width: 200px !important;
}

.continue-btn >>> .v-btn__content {
  color: #ffffff;
  font-size: 15px !important;
  font-weight: 600 !important;
  gap: 10px;
}

/* Card */
.custom-p-vendor-signup {
  padding: 14px 22px 12px !important;
}

/* Fullscreen-only sizing: locks the card to the viewport when this is
   the standalone /my-market or /select_market route. Embedded admin
   usage gets a natural-height card so the parent layout (table below)
   isn't pushed off-screen. */
.sign-up_owner .custom-container {
  max-width: calc(100vw - 30px) !important;
  width: calc(100vw - 30px);
  height: calc(100vh - 30px);
  top: 0 !important;
  margin: 15px auto !important;
  display: flex !important;
  flex-direction: column;
  overflow: hidden;
}


@media (max-width: 700px) {
  .header-row {
    flex-direction: column;
    gap: 4px;
  }
  /* Price block is a single inline row on phones — amount + period on the
     left, the bolded $1/$295/Exclusive note flowing immediately after,
     wrapping if needed. Saves the vertical line a stand-alone <p> would
     consume. */
  .price-block {
    text-align: left;
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px 10px;
  }
  .price-block .price-note {
    flex: 1 1 200px;
    margin: 0;
    font-size: 11px;
    line-height: 1.3;
  }
  /* Map sized so the whole United States is comfortably visible on
     phones (~75% taller than the previous 250px max). clamp keeps it
     reasonable across phone-to-tablet widths. */
  .map-container {
    flex: 0 0 auto;
    display: block;
    margin-top: 8px;
  }
  .market-map {
    width: 100%;
    height: clamp(320px, 45vh, 440px);
  }
  /* Top controls stack: search row, then the paired draw + claim buttons. */
  .top-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  /* Outline / Redraw stay content-sized on phones (small pill, ~150px),
     while the Claim CTA sits to its right and grows to fill the remaining
     row width — Claim is the visual anchor of the row. */
  .draw-buttons {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }
  .draw-buttons .draw-btn:not(.draw-btn-claim) {
    flex: 0 0 auto;
  }
  .draw-buttons .draw-btn-claim {
    flex: 1 1 auto;
    min-width: 0;
  }
  .draw-buttons .draw-btn .v-btn__content {
    letter-spacing: 0 !important;
  }
  /* Claim CTA: bigger font + slightly taller pill so it reads as the
     primary action paired with the smaller Outline / Redraw button. */
  .draw-buttons .draw-btn-claim.v-btn {
    height: 38px !important;
  }
  .draw-buttons .draw-btn-claim .v-btn__content {
    font-size: 15px !important;
    font-weight: 700 !important;
  }
  /* Card sizes to its content on phones — header is one row now, map is a
     fixed clamp, so the total fits inside any phone viewport without
     locking to 100vh. */
  .sign-up_owner .custom-container {
    height: auto !important;
    max-height: none !important;
    max-width: calc(100vw - 8px) !important;
    width: calc(100vw - 8px);
    margin: 4px auto !important;
    overflow: visible !important;
  }
  /* Hide the bottom desktop actions row entirely on phones — Claim CTA
     lives inline next to Outline my market via .draw-btn-claim. */
  .actions {
    display: none;
  }
  /* Page should land at the top on mobile, not vertically centred. */
  .sign-up_owner .row {
    align-items: flex-start !important;
    min-height: 0 !important;
  }
  .stepper {
    margin-bottom: 4px;
  }
  .step {
    min-width: 56px;
  }
  .step-line {
    flex: 0 1 40px;
  }
  /* Compress header so the map gets the lion's share of the screen. */
  .price-note {
    font-size: 11px;
    line-height: 1.35;
  }
  .market-subtitle {
    font-size: 12px;
    line-height: 1.35;
  }
  .price-amount {
    font-size: 20px;
  }
  .price-period {
    font-size: 14px;
  }
  .title_agent {
    font-size: 18px;
    line-height: 22px;
    margin-bottom: 2px;
  }
  /* Tighter card padding on phones so the map can fill the screen. */
  .custom-p-vendor-signup {
    padding: 10px 12px 10px !important;
  }
  .territory-disclaimer {
    font-size: 10px;
    margin-top: 4px;
  }
  .readout {
    padding: 6px 12px;
  }
  .readout-line {
    font-size: 13px;
  }
  /* Stack the value-prop list into a single column on phones — bigger
     touch-target line height for readability, sits as a card between
     the price block and the map controls. */
  .my-market-includes {
    padding: 10px 12px;
    margin-bottom: 8px;
  }
  .my-market-includes-title {
    font-size: 12px;
    margin-bottom: 6px;
  }
  .my-market-includes-list {
    grid-template-columns: 1fr;
    row-gap: 6px;
  }
  .my-market-includes-list li {
    font-size: 13px;
    line-height: 1.5;
    padding-left: 24px;
  }
}

/* Waitlist modal — opens when a public visitor clicks a Reserved polygon */
.wl-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
}
.wl-modal {
  position: relative;
  background: #fff;
  border-radius: 14px;
  padding: 28px 28px 24px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  font-family: 'Lato', system-ui, sans-serif;
}
.wl-close {
  position: absolute;
  top: 8px;
  right: 12px;
  background: none;
  border: none;
  font-size: 28px;
  line-height: 1;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px 8px;
}
.wl-close:hover { color: #0f172a; }
.wl-title {
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 700;
  color: #061d47;
}
.wl-sub {
  margin: 0 0 18px;
  font-size: 14px;
  color: #475569;
  line-height: 1.45;
}
.wl-row { display: flex; gap: 10px; }
.wl-row > label { flex: 1; }
.wl-field, .wl-row label {
  display: block;
  margin-bottom: 12px;
}
.wl-field span, .wl-row label span {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 4px;
}
.wl-field input, .wl-row input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  font-weight: 600;
  color: #061D47;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.wl-field input:focus, .wl-row input:focus { border-color: #061d47; }
.wl-error {
  margin: 4px 0 12px;
  font-size: 13px;
  color: #b91c1c;
}
.wl-submit {
  width: 100%;
  background: #061d47;
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 12px 18px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  margin-top: 4px;
}
.wl-submit:disabled { opacity: 0.6; cursor: not-allowed; }
.wl-submit:hover:not(:disabled) { background: #0a2a66; }
</style>

<style>
/* Lock the /select_market page to the viewport — no scroll. */
.sign-up_owner,
.sign-up_owner .v-content__wrap {
  height: 100vh !important;
  padding: 0 !important;
  overflow: hidden;
}

.sign-up_owner .container,
.sign-up_owner .container--fluid {
  padding: 0 !important;
  margin: 0 !important;
  height: 100vh;
  max-width: 100% !important;
}

.sign-up_owner .row {
  margin: 0 !important;
  height: 100vh;
  flex-wrap: nowrap;
}

.sign-up_owner .v-card.custom-container {
  padding: 14px 22px 12px !important;
}

/* Mobile: drop the viewport lock so the bottom CTA is reachable by scroll.
   Desktop keeps the fullscreen, no-scroll layout. */
@media (max-width: 700px) {
  .sign-up_owner,
  .sign-up_owner .v-content__wrap {
    height: auto !important;
    min-height: 100vh;
    overflow: visible !important;
  }
  .sign-up_owner .container,
  .sign-up_owner .container--fluid {
    height: auto !important;
    min-height: 100vh;
  }
  .sign-up_owner .row {
    height: auto !important;
    min-height: 100vh;
    flex-wrap: wrap;
  }
  .sign-up_owner .v-card.custom-container {
    padding: 12px 14px 16px !important;
  }
}

/* Admin-mode inline embedding: when MarketSelection is rendered inside
   the super-admin Markets page (not as a standalone /my-market route),
   it must NOT lock to the viewport — let the parent slot decide its
   height instead. Otherwise the 100vh rules above push it past the
   parent .map-wrap and the page header/table look replaced. */
.sign-up_owner.admin-inline,
.sign-up_owner.admin-inline .v-content__wrap,
.sign-up_owner.admin-inline .container,
.sign-up_owner.admin-inline .container--fluid,
.sign-up_owner.admin-inline .row {
  height: auto !important;
  min-height: 0 !important;
  max-height: none !important;
  overflow: visible !important;
}
/* v-content (rendered as <main class="v-content">) inherits the outer
   v-app's sidebar/header offsets via inline styles — neutralize those
   when we're embedded in the super-admin Markets page slot. Also force
   `position: static` so it lays out as a normal block child of
   .map-wrap instead of escaping to the v-app coordinate system. */
.sign-up_owner.admin-inline.v-content,
main.sign-up_owner.admin-inline {
  display: block !important;
  position: static !important;
  padding: 0 !important;
  margin: 0 !important;
}
.sign-up_owner.admin-inline .custom-container {
  height: auto !important;
  min-height: 560px;
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 !important;
}
/* The standalone /my-market layout sizes the map via 100vh chains.
   Embedded in the admin slot, those chains collapse to 0 — so the map
   div renders with zero height and Google Maps refuses to paint tiles.
   Give it an explicit pixel height in admin-inline mode. */
.sign-up_owner.admin-inline .market-map {
  height: 480px !important;
  min-height: 480px;
}

/* Google Places Autocomplete dropdown — pin BELOW the search input.
   We reparent the pac-container into .search-wrapper (position: relative)
   and use !important to override Google's inline absolute positioning. */
.search-wrapper .pac-container.kiey-pac-below {
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  right: 0 !important;
  width: auto !important;
  margin-top: 4px;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  z-index: 1000;
}
/* The waitlist modal sits at z-index 9999, but Google's Places
   autocomplete .pac-container is appended to <body> with its default
   z-index 1000 — it would render *behind* the modal and look like the
   dropdown isn't there at all. Bump it above the overlay. */
.pac-container {
  z-index: 10000 !important;
}

/* Share popover — v-menu teleports its content to <body>, so scoped CSS
   from this component never reaches it. Everything styling the popover
   lives here so it's immune to parent-component style bleed and works
   inside the Vuetify overlay portal. */
.share-popover-wrap .share-popover {
  padding: 16px 18px;
  width: 280px;
}
.share-popover-wrap .share-title {
  font-size: 15px;
  font-weight: 700;
  color: #061D47;
  margin: 0 0 4px;
  text-transform: none;
  letter-spacing: 0;
  line-height: 1.3;
}
.share-popover-wrap .share-body {
  font-size: 12px;
  color: #5d5d5d;
  margin: 0 0 12px;
  text-transform: none;
  letter-spacing: 0;
  line-height: 1.4;
}
.share-popover-wrap .share-actions .v-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  font-size: 13px !important;
}
.share-popover-wrap .share-actions .v-btn + .v-btn {
  margin-top: 6px;
}
.share-popover-wrap .reserve-btn {
  font-weight: 700 !important;
}
.share-popover-wrap .share-divider {
  display: flex;
  align-items: center;
  margin: 10px 0 8px;
  color: #9aa2b4;
  font-size: 10px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  font-weight: 600;
}
.share-popover-wrap .share-divider::before,
.share-popover-wrap .share-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e4e8f1;
}
.share-popover-wrap .share-divider span {
  padding: 0 8px;
}

/* On phones the v-menu's 280px popover floats over the map and gets lost
   visually — Vuetify positions it absolutely above the Done button, but
   the cramped width hides the actions. Force it to span the viewport so
   buttons are tappable and the modal reads as the next step. */
@media (max-width: 700px) {
  .share-popover-wrap {
    left: 8px !important;
    right: 8px !important;
    max-width: calc(100vw - 16px) !important;
    min-width: 0 !important;
    width: calc(100vw - 16px);
  }
  .share-popover-wrap .share-popover {
    width: 100%;
    padding: 18px 18px 16px;
  }
  .share-popover-wrap .share-title {
    font-size: 17px;
  }
  .share-popover-wrap .share-body {
    font-size: 13px;
  }
}
</style>
